import { useEffect, useState, useRef } from "react";
import {
  ClerkProvider,
  SignedIn,
  UserButton,
  useAuth,
} from "@clerk/clerk-react";
import { setAuthTokenResolver } from "@core/services/http";
import { useFarmStore } from "./useFarmStore";
import { useCitizenStore } from "./useCitizenStore";
import { ParcelMap } from "./components/ParcelMap";
import { CitizenMap } from "./components/CitizenMap";
import type { ParcelItem } from "@core/services/parcel.service";
import type { AnalysisRunResponse, SceneObservationItem, ParcelAnomalyItem } from "@core/services/analysis.service";

export interface UserProfile {
  name: string;
  email: string;
  role: "citizen" | "farmer" | "nursery";
  farmName?: string;
  avatar?: string;
  points?: number;
}

export const DEMO_USERS: Record<"citizen" | "farmer" | "nursery", UserProfile> = {
  citizen: {
    name: "Maya Chen",
    email: "citizen@econinhursag.org",
    role: "citizen",
    farmName: "Riverside District Community",
    avatar: "https://images.unsplash.com/photo-1534528741775-53994a69daeb?crop=entropy&cs=tinysrgb&fit=crop&w=300&h=300&q=80",
    points: 1250,
  },
  farmer: {
    name: "Samir Khalil",
    email: "farmer@econinhursag.ma",
    role: "farmer",
    farmName: "Green Valley Farm · Al Haouz",
    avatar: "https://images.unsplash.com/photo-1507003211169-0a1dd7228f2d?crop=entropy&cs=tinysrgb&fit=crop&w=300&h=300&q=80",
    points: 0,
  },
  nursery: {
    name: "Layla Alami",
    email: "nursery@econinhursag.ma",
    role: "nursery",
    farmName: "Green Garden Nursery & Seed Co.",
    avatar: "https://images.unsplash.com/photo-1544005313-94ddf0286df2?crop=entropy&cs=tinysrgb&fit=crop&w=300&h=300&q=80",
    points: 0,
  },
};

export function getInitialUser(): UserProfile {
  if (typeof window === "undefined") return DEMO_USERS.farmer;
  try {
    const raw = window.sessionStorage.getItem("eco-user") || window.localStorage.getItem("eco-user-profile");
    if (raw) {
      const parsed = JSON.parse(raw);
      if (parsed && parsed.name && parsed.role) return parsed;
    }
  } catch {}
  return DEMO_USERS.farmer;
}

const CLERK_PUBLISHABLE_KEY = (import.meta as any).env?.VITE_CLERK_PUBLISHABLE_KEY || "";

function AuthTokenSync() {
  const { getToken, isSignedIn } = useAuth();
  useEffect(() => {
    if (isSignedIn) {
      window.sessionStorage.setItem("eco-authenticated", "true");
      setAuthTokenResolver(async () => {
        try {
          return await getToken();
        } catch {
          return null;
        }
      });
    } else {
      setAuthTokenResolver(null);
    }
  }, [isSignedIn, getToken]);
  return null;
}

type Screen =
  | "welcome" | "signup" | "home" | "explore" | "learn" | "community" | "profile"
  | "mission" | "rewards" | "notifications" | "farmer-notifications" | "nursery-notifications"
  | "farmer-overview" | "fields" | "risks" | "advice" | "farm-profile"
  | "assistant" | "nursery-overview" | "products" | "product-detail" | "orders" | "store" | "business-profile" | "marketplace";

type IconName =
  | "home" | "compass" | "book" | "people" | "user" | "map" | "alert" | "gift"
  | "globe" | "bell" | "chevron" | "leaf" | "drop" | "plus" | "search" | "back"
  | "shield" | "gear" | "pin" | "spark" | "chat" | "heart" | "bookmark" | "eye"
  | "check" | "lock" | "clock" | "flame" | "box" | "cart" | "chart" | "field";

const PHOTO_GARDEN = "https://images.unsplash.com/photo-1621460248083-6271cc4437a8?crop=entropy&cs=tinysrgb&fit=crop&w=900&h=900&q=85";
const PHOTO_PLANTING = "https://images.unsplash.com/photo-1524247108137-732e0f642303?crop=entropy&cs=tinysrgb&fit=crop&w=900&h=900&q=85";
const PHOTO_PARK = "https://images.unsplash.com/photo-1758798482337-bd7c38e94186?crop=entropy&cs=tinysrgb&fit=crop&w=900&h=900&q=85";
const PHOTO_HANDS = "https://images.unsplash.com/photo-1622383563227-04401ab4e5ea?crop=entropy&cs=tinysrgb&fit=crop&w=900&h=900&q=85";
const PHOTO_FIELD = "https://images.unsplash.com/photo-1508175688576-0c076b47b5b5?crop=entropy&cs=tinysrgb&fit=crop&w=1000&h=600&q=85";
const PHOTO_STORE = "https://images.unsplash.com/photo-1601985705806-5b9a71f6004f?crop=entropy&cs=tinysrgb&fit=crop&w=1000&h=600&q=85";
const PHOTO_PRODUCT = "https://images.unsplash.com/photo-1683994851774-6e9642fb8a95?crop=entropy&cs=tinysrgb&fit=crop&w=500&h=500&q=85";
const PHOTO_GREENHOUSE = "https://images.unsplash.com/photo-1668962225017-12ef9d7981c2?crop=entropy&cs=tinysrgb&fit=crop&w=500&h=500&q=85";

const paths: Record<IconName, React.ReactNode> = {
  home: <><path d="m3 11 9-8 9 8"/><path d="M5 10v11h14V10M9 21v-7h6v7"/></>,
  compass: <><circle cx="12" cy="12" r="9"/><path d="m16 8-2.4 5.6L8 16l2.4-5.6L16 8Z"/></>,
  book: <><path d="M4 5a3 3 0 0 1 3-3h4v18H7a3 3 0 0 0-3 2V5ZM20 5a3 3 0 0 0-3-3h-4v18h4a3 3 0 0 1 3 2V5Z"/></>,
  people: <><circle cx="9" cy="8" r="4"/><path d="M2 21a7 7 0 0 1 14 0M17 4a4 4 0 0 1 0 8M22 21a6 6 0 0 0-5-5"/></>,
  user: <><circle cx="12" cy="8" r="4"/><path d="M4 21a8 8 0 0 1 16 0"/></>,
  map: <><path d="m3 6 6-3 6 3 6-3v15l-6 3-6-3-6 3V6Z"/><path d="M9 3v15M15 6v15"/></>,
  alert: <><path d="M12 3 2.5 20h19L12 3Z"/><path d="M12 9v5M12 17h.01"/></>,
  gift: <><path d="M3 10h18v11H3V10ZM2 6h20v4H2V6ZM12 6v15"/><path d="M12 6H8.5A2.5 2.5 0 1 1 11 3.5L12 6Zm0 0h3.5A2.5 2.5 0 1 0 13 3.5L12 6Z"/></>,
  globe: <><circle cx="12" cy="12" r="9"/><path d="M3 12h18M12 3a15 15 0 0 1 0 18M12 3a15 15 0 0 0 0 18"/></>,
  bell: <><path d="M18 8a6 6 0 0 0-12 0c0 7-3 7-3 9h18c0-2-3-2-3-9M10 21h4"/></>,
  chevron: <path d="m9 18 6-6-6-6"/>,
  leaf: <><path d="M20 4C11 4 5 8 5 14c0 3 2 5 5 5 6 0 10-6 10-15Z"/><path d="M4 21c3-6 7-9 12-12"/></>,
  drop: <path d="M12 2S5 10 5 15a7 7 0 0 0 14 0c0-5-7-13-7-13Z"/>,
  plus: <path d="M12 5v14M5 12h14"/>,
  search: <><circle cx="11" cy="11" r="7"/><path d="m20 20-4-4"/></>,
  back: <><path d="m15 18-6-6 6-6M9 12h11"/></>,
  shield: <><path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10Z"/><path d="m9 12 2 2 4-4"/></>,
  gear: <><circle cx="12" cy="12" r="3"/><path d="M19.4 15a2 2 0 0 0 .4 2.2l.1.1-2.6 2.6-.1-.1a2 2 0 0 0-2.2-.4 2 2 0 0 0-1.2 1.8V21h-3.6v-.2A2 2 0 0 0 9 19a2 2 0 0 0-2.2.4l-.1.1-2.6-2.6.1-.1A2 2 0 0 0 4.6 15a2 2 0 0 0-1.8-1.2H2v-3.6h.8A2 2 0 0 0 4.6 9a2 2 0 0 0-.4-2.2l-.1-.1 2.6-2.6.1.1A2 2 0 0 0 9 4.6a2 2 0 0 0 1.2-1.8V2h3.6v.8A2 2 0 0 0 15 4.6a2 2 0 0 0 2.2-.4l.1-.1 2.6 2.6-.1.1A2 2 0 0 0 19.4 9a2 2 0 0 0 1.8 1.2h.8v3.6h-.8A2 2 0 0 0 19.4 15Z"/></>,
  pin: <><path d="M20 10c0 5-8 12-8 12S4 15 4 10a8 8 0 1 1 16 0Z"/><circle cx="12" cy="10" r="2.5"/></>,
  spark: <><path d="m12 3 1.3 4.7L18 9l-4.7 1.3L12 15l-1.3-4.7L6 9l4.7-1.3L12 3Z"/><path d="m19 16 .7 2.3L22 19l-2.3.7L19 22l-.7-2.3L16 19l2.3-.7L19 16Z"/></>,
  chat: <path d="M21 15a4 4 0 0 1-4 4H8l-5 3V7a4 4 0 0 1 4-4h10a4 4 0 0 1 4 4v8Z"/>,
  heart: <path d="M20.8 5.7a5.5 5.5 0 0 0-7.8 0L12 6.8l-1.1-1.1a5.5 5.5 0 0 0-7.8 7.8L12 22l8.8-8.5a5.5 5.5 0 0 0 0-7.8Z"/>,
  bookmark: <path d="M6 3h12v19l-6-4-6 4V3Z"/>,
  eye: <><path d="M2 12s3.5-6 10-6 10 6 10 6-3.5 6-10 6S2 12 2 12Z"/><circle cx="12" cy="12" r="2.5"/></>,
  check: <path d="m5 12 4 4L19 6"/>,
  lock: <><rect x="5" y="10" width="14" height="11" rx="2"/><path d="M8 10V7a4 4 0 0 1 8 0v3"/></>,
  clock: <><circle cx="12" cy="12" r="9"/><path d="M12 7v5l3 2"/></>,
  flame: <path d="M13 22c4-1 7-4 7-8 0-3-2-6-5-9 0 3-2 5-3 6 0-4-2-7-4-9 0 5-4 7-4 12 0 4 3 7 7 8-2-1-3-3-2-5 1-2 3-3 3-5 2 2 3 4 3 6 0 2-1 3-2 4Z"/>,
  box: <><path d="m4 7 8-4 8 4-8 4-8-4Z"/><path d="M4 7v10l8 4 8-4V7M12 11v10"/></>,
  cart: <><path d="M3 3h2l2 12h11l2-8H6M9 21h.01M18 21h.01"/></>,
  chart: <><path d="M4 20V10M10 20V4M16 20v-7M22 20V7"/></>,
  field: <><path d="M3 20c4-7 7-10 9-16M8 21c3-6 7-10 13-13M14 21c2-4 4-6 7-8"/></>,
};

function Icon({ name, size = 22 }: { name: IconName; size?: number }) {
  return <svg className="icon" aria-hidden="true" viewBox="0 0 24 24" width={size} height={size}>{paths[name]}</svg>;
}

function Button({ children, onClick, kind = "primary", className = "", label, type = "button", disabled = false, style }: { children: React.ReactNode; onClick?: () => void; kind?: "primary" | "outline" | "text" | "dark" | "icon"; className?: string; label?: string; type?: "button" | "submit"; disabled?: boolean; style?: React.CSSProperties }) {
  return <button type={type} aria-label={label} className={`button ${kind} ${className}`} onClick={onClick} disabled={disabled} style={style}>{children}</button>;
}

function Logo() {
  return <span className="logo" aria-label="EcoNinhursag logo"><b>EN</b><i/></span>;
}

function Topo() {
  return <svg className="topo" viewBox="0 0 350 180" preserveAspectRatio="none" aria-hidden="true">{[0, 1, 2, 3].map(i => <path key={i} d={`M-20 ${35 + i * 30} C60 ${5 + i * 31},100 ${70 + i * 23},180 ${35 + i * 28} S300 ${25 + i * 25},380 ${55 + i * 25}`}/>)}</svg>;
}

function Pill({ children, tone = "sage" }: { children: React.ReactNode; tone?: "sage" | "peach" | "dark" | "mist" }) {
  return <span className={`pill ${tone}`}>{children}</span>;
}

function Progress({ value, dark = false }: { value: number; dark?: boolean }) {
  return <span className={`progress ${dark ? "on-dark" : ""}`}><i style={{ width: `${value}%` }}/></span>;
}

function Ring({ value, alert = false }: { value: number; alert?: boolean }) {
  return <span className={`ring ${alert ? "alert" : ""}`} style={{ "--value": `${value * 3.6}deg` } as React.CSSProperties}><b>{value}%</b></span>;
}

function TopBar({
  title,
  action,
  actionLabel = "Settings",
  onLogout,
  user,
}: {
  title: string;
  action?: React.ReactNode;
  actionLabel?: string;
  onLogout?: () => void;
  user?: UserProfile | null;
}) {
  const isAuth = typeof window !== "undefined" && window.sessionStorage.getItem("eco-authenticated") === "true";
  const activeUser = user !== undefined ? user : (isAuth ? getInitialUser() : null);
  const handleLogout = () => {
    if (onLogout) {
      onLogout();
    } else {
      window.sessionStorage.removeItem("eco-authenticated");
      window.sessionStorage.removeItem("eco-user");
      window.location.reload();
    }
  };

  const firstName = activeUser?.name ? activeUser.name.split(" ")[0] : null;

  return (
    <header className="topbar">
      <div style={{ display: "flex", alignItems: "center", gap: 7 }}>
        <Logo />
        {activeUser?.avatar ? (
          <img
            src={activeUser.avatar}
            alt={activeUser.name || "User"}
            style={{
              width: 26,
              height: 26,
              borderRadius: "50%",
              objectFit: "cover",
              border: "1.5px solid var(--forest-600, #2E6F40)",
              boxShadow: "0 1px 4px rgba(0,0,0,0.1)",
            }}
          />
        ) : null}
      </div>
      <strong>{title}</strong>
      <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
        {CLERK_PUBLISHABLE_KEY ? (
          <SignedIn>
            <UserButton />
          </SignedIn>
        ) : null}
        {firstName ? (
          <span style={{ fontSize: "11px", fontWeight: 700, color: "var(--forest-900, #14281d)", opacity: 0.9 }}>
            {firstName}
          </span>
        ) : null}
        {isAuth ? (
          <button
            type="button"
            aria-label="Log out"
            onClick={handleLogout}
            style={{
              background: "rgba(255, 255, 255, 0.9)",
              border: "1px solid rgba(26, 51, 38, 0.2)",
              borderRadius: "12px",
              padding: "4px 9px",
              fontSize: "11px",
              fontWeight: 700,
              color: "var(--forest-900, #14281d)",
              cursor: "pointer",
            }}
          >
            Log out
          </button>
        ) : null}
        {action ? <Button kind="icon" label={actionLabel}>{action}</Button> : <span />}
      </div>
    </header>
  );
}

const citizenNav: [string, IconName, Screen][] = [["Home", "home", "home"], ["Explore", "compass", "explore"], ["Learn", "book", "learn"], ["Community", "people", "community"], ["Profile", "user", "profile"]];
const farmerNav: [string, IconName, Screen][] = [["Overview", "home", "farmer-overview"], ["Fields", "field", "fields"], ["Risks", "alert", "risks"], ["Advice", "spark", "advice"], ["Profile", "user", "farm-profile"]];
const nurseryNav: [string, IconName, Screen][] = [["Overview", "home", "nursery-overview"], ["Products", "leaf", "products"], ["Orders", "box", "orders"], ["Store", "globe", "store"], ["Profile", "user", "business-profile"]];

const pageNotes: Partial<Record<Screen, [IconName, string, string]>> = {
  home: ["leaf", "Today’s growth idea", "Choose one nearby action and invite one person to join."],
  explore: ["map", "Build a greener route", "Combine two nearby missions into one low-travel morning."],
  learn: ["spark", "Learn, then act", "Save one practical idea and try it before your next lesson."],
  community: ["people", "Keep it useful", "Share what worked, add context, and celebrate progress."],
  profile: ["gift", "Your impact has a story", "Share one result to help another guardian begin."],
  "farmer-overview": ["field", "Observe before acting", "Use sensor trends to guide a short field check."],
  fields: ["map", "Compare, don’t guess", "Check field edges against central growing zones."],
  risks: ["shield", "Plan for the peak", "Prepare a low-effort response before conditions intensify."],
  advice: ["check", "Close the loop", "Record what you observed after each completed action."],
  "farm-profile": ["globe", "Better data, better guidance", "Keep crop, boundary and sensor details current."],
  "nursery-overview": ["chart", "Turn attention into trust", "Respond quickly to stock and pickup requests."],
  products: ["leaf", "Make value visible", "Lead with care needs, origin and environmental benefits."],
  orders: ["clock", "Make pickup feel personal", "Confirm timing early and prepare a clear collection point."],
  store: ["heart", "Tell your green story", "Feature practices customers can understand and verify."],
  "business-profile": ["shield", "Trust compounds", "Keep verification, policies and store details transparent."],
};

function Nav({ current, go, role }: { current: Screen; go: (s: Screen) => void; role: "citizen" | "farmer" | "nursery" }) {
  const items = role === "citizen" ? citizenNav : role === "farmer" ? farmerNav : nurseryNav;
  return <nav className="bottom-nav" aria-label={`${role} navigation`}>{items.map(([label, icon, screen]) => <Button key={screen} kind="text" label={label} className={current === screen ? "active" : ""} onClick={() => go(screen)}><Icon name={icon}/><span>{label}</span></Button>)}</nav>;
}

function Page({ children, screen, role, go, className = "" }: { children: React.ReactNode; screen: Screen; role?: "citizen" | "farmer" | "nursery"; go: (s: Screen) => void; className?: string }) {
  const note = pageNotes[screen];
  return <main className={`page ${role ? `with-nav role-${role}` : ""} ${className}`}><div className="scroll" key={screen}>{children}{role && note && <aside className="page-note"><i><Icon name={note[0]} size={19}/></i><div><b>{note[1]}</b><span>{note[2]}</span></div></aside>}</div>{role && <Nav current={screen} go={go} role={role}/>}</main>;
}

function SectionHead({ title, action }: { title: string; action?: string }) {
  return <div className="section-head"><h2>{title}</h2>{action && <Button kind="text">{action}</Button>}</div>;
}

function ContextStrip({ icon, title, text, tone = "" }: { icon: IconName; title: string; text: string; tone?: "peach" | "dark" | "" }) {
  return <section className={`context-strip ${tone}`}><i><Icon name={icon} size={19}/></i><div><b>{title}</b><span>{text}</span></div><Icon name="chevron" size={17}/></section>;
}

function ImpactPlant({ grown }: { grown: boolean }) {
  return <div className={`impact-plant ${grown ? "grown" : ""}`} aria-label={grown ? "Your impact plant grew after a completed mission" : "Your impact plant is ready to grow"}><i className="plant-glow"/><i className="plant-soil"/><i className="plant-stem"/><i className="plant-leaf left"/><i className="plant-leaf right"/><i className="plant-leaf top"/><i className="plant-bloom"><Icon name="spark" size={14}/></i></div>;
}

function MissionCard({ title = "Restore Riverside Park", place = "Riverside Park", level = "Easy", points = "+100", image = PHOTO_PARK, onClick }: { title?: string; place?: string; level?: string; points?: string; image?: string; onClick?: () => void }) {
  return <Button kind="text" className="mission-card" onClick={onClick}><span className="mission-art"><img src={image} alt=""/><i/><Icon name="leaf" size={30}/></span><span className="mission-copy"><small>{place} · 1.2 km</small><strong>{title}</strong><span><Pill>{level}</Pill><Pill tone="peach">{points} pts</Pill></span></span><span className="save"><Icon name="bookmark" size={18}/></span><span className="mission-meta"><span><Icon name="clock" size={16}/> Sat · 10:00 <i/> 18 joined</span><b>4 spots left</b></span><Progress value={72}/></Button>;
}

function Stats({ items }: { items: [string, string][] }) {
  return <section className="stats">{items.map(([value, label]) => <div key={label}><b>{value}</b><span>{label}</span></div>)}</section>;
}

function Header({ eyebrow, title, caption, bell = true, onBell }: { eyebrow: string; title: string; caption: string; bell?: boolean; onBell?: () => void }) {
  return <header className="page-header"><div><span className="eyebrow">{eyebrow}</span><h1>{title}</h1><p>{caption}</p></div>{bell && <Button kind="icon" label="Notifications" onClick={onBell}><Icon name="bell"/><i className="notice"/></Button>}</header>;
}

const welcomeSlides = [
  ["Welcome to EcoNinhursag", "Grow impact from wherever you are.", "A simple place to discover, act, learn and grow your local impact.", PHOTO_GARDEN],
  ["Discover", "Find environmental missions near you.", "See meaningful actions and greener places right around the corner.", PHOTO_PARK],
  ["Act and earn", "Turn small actions into visible progress.", "Complete missions, collect EcoPoints and watch your contribution grow.", PHOTO_HANDS],
  ["Learn and connect", "Protect vegetation, together.", "Build practical knowledge with a community that cares.", PHOTO_PLANTING],
];

function Welcome({ go }: { go: (s: Screen) => void }) {
  const [slide, setSlide] = useState(0);
  const item = welcomeSlides[slide];
  return <Page screen="welcome" go={go} className="welcome"><section className="welcome-art"><img src={item[3]} alt="People caring for plants together"/><i className="photo-overlay"/><Topo/><div className="brand-lockup"><Logo/><b>EcoNinhursag</b></div><Button kind="text" onClick={() => go("signup")}>Sign in</Button></section><section className="welcome-copy" key={slide}><span className="eyebrow">{item[0]}</span><h1>{item[1]}</h1><p>{item[2]}</p><div className="welcome-actions"><span className="dots">{welcomeSlides.map((_, i) => <i key={i} className={i === slide ? "active" : ""}/>)}</span><Button onClick={() => slide < 3 ? setSlide(slide + 1) : go("signup")}>{slide === 3 ? "Create account" : "Next"} <Icon name="chevron"/></Button></div></section></Page>;
}

function Signup({
  go,
  onUserChange,
  currentUser,
}: {
  go: (s: Screen) => void;
  onUserChange?: (u: UserProfile) => void;
  currentUser?: UserProfile;
}) {
  const [tab, setTab] = useState<"signin" | "signup">("signin");
  const [show, setShow] = useState(false);
  const [role, setRole] = useState<"citizen" | "farmer" | "nursery">(currentUser?.role || "farmer");
  const [name, setName] = useState(currentUser?.name || "Samir Khalil");
  const [email, setEmail] = useState(currentUser?.email || "farmer@econinhursag.ma");
  const [password, setPassword] = useState("EcoNinhursag2026!");
  const [farmName, setFarmName] = useState(currentUser?.farmName || "Green Valley Farm · Al Haouz");
  const [avatar, setAvatar] = useState<string>(currentUser?.avatar || DEMO_USERS.farmer.avatar || "");
  const [agreed, setAgreed] = useState(true);
  const [attempted, setAttempted] = useState(false);
  const [errorMsg, setErrorMsg] = useState("");
  const fileInputRef = useRef<HTMLInputElement>(null);

  const emailValid = /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email);
  const passwordValid = password.length >= 6;
  const nameValid = name.trim().length > 0;
  const valid = emailValid && passwordValid && (tab === "signin" || (nameValid && agreed));

  const destination: Screen =
    role === "farmer" ? "farmer-overview" : role === "nursery" ? "nursery-overview" : "home";

  const handlePickDemo = (selectedRole: "citizen" | "farmer" | "nursery") => {
    const demo = DEMO_USERS[selectedRole];
    setRole(selectedRole);
    setName(demo.name);
    setEmail(demo.email);
    setPassword("EcoNinhursag2026!");
    setFarmName(demo.farmName || "");
    setAvatar(demo.avatar || "");
    setErrorMsg("");
  };

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      if (file.size > 5 * 1024 * 1024) {
        setErrorMsg("Photo file size must be under 5 MB.");
        return;
      }
      const reader = new FileReader();
      reader.onload = () => {
        if (typeof reader.result === "string") {
          setAvatar(reader.result);
          setErrorMsg("");
        }
      };
      reader.readAsDataURL(file);
    }
  };

  const enter = () => {
    if (tab === "signup" && !nameValid) {
      setErrorMsg("Please enter your full name.");
      return;
    }
    if (!emailValid) {
      setErrorMsg("Please enter a valid email address.");
      return;
    }
    if (!passwordValid) {
      setErrorMsg("Password must be at least 6 characters.");
      return;
    }
    if (tab === "signup" && !agreed) {
      setErrorMsg("Please accept the terms to continue.");
      return;
    }

    const fallbackAvatar = DEMO_USERS[role]?.avatar || "";
    const finalProfile: UserProfile = {
      name: name.trim() || (role === "citizen" ? "Maya Chen" : role === "farmer" ? "Samir Khalil" : "Layla Alami"),
      email: email.trim(),
      role,
      farmName: farmName.trim() || (role === "citizen" ? "Riverside Community" : role === "farmer" ? "Green Valley Farm · Al Haouz" : "Green Garden Nursery"),
      avatar: avatar || fallbackAvatar,
      points: role === "citizen" ? 1250 : 0,
    };

    if (onUserChange) {
      onUserChange(finalProfile);
    } else {
      window.sessionStorage.setItem("eco-user", JSON.stringify(finalProfile));
      window.sessionStorage.setItem("eco-authenticated", "true");
      window.localStorage.setItem("eco-user-profile", JSON.stringify(finalProfile));
    }

    go(destination);
  };

  const submit = (e: React.FormEvent) => {
    e.preventDefault();
    setAttempted(true);
    if (valid) {
      enter();
    } else {
      if (tab === "signup" && !nameValid) setErrorMsg("Enter your full name.");
      else if (!emailValid) setErrorMsg("Enter a valid email address.");
      else if (!passwordValid) setErrorMsg("Password must be at least 6 characters.");
      else if (tab === "signup" && !agreed) setErrorMsg("Please accept the terms to continue.");
    }
  };

  return (
    <Page screen="signup" go={go} className="signup">
      <header className="auth-top">
        <Logo />
        <span style={{ fontSize: "13px", fontWeight: 700, color: "var(--forest-800)" }}>
          EcoNinhursag Platform
        </span>
      </header>

      {/* Sign In vs Create Account Toggle Tabs */}
      <div className="auth-tabs" role="tablist">
        <button
          type="button"
          className={`auth-tab ${tab === "signin" ? "active" : ""}`}
          onClick={() => {
            setTab("signin");
            setErrorMsg("");
          }}
        >
          Sign In
        </button>
        <button
          type="button"
          className={`auth-tab ${tab === "signup" ? "active" : ""}`}
          onClick={() => {
            setTab("signup");
            setErrorMsg("");
            if (name === "Samir Khalil") setName("");
            if (email === "farmer@econinhursag.ma") setEmail("");
          }}
        >
          Create Account
        </button>
      </div>

      <span className="eyebrow">
        {tab === "signin" ? "Welcome Back · Select Your Profile" : "Join EcoNinhursag"}
      </span>
      <h1 style={{ fontSize: "24px", marginBottom: "4px" }}>
        {tab === "signin" ? "Sign In to Your Workspace" : "Create Your Account"}
      </h1>
      <p className="signup-intro" style={{ marginBottom: "14px", fontSize: "13px" }}>
        {tab === "signin"
          ? "Choose a demo persona to test instantly, or sign in with your email."
          : "Sign up to track vegetation, complete missions, or monitor your parcels."}
      </p>

      {/* Role Selection */}
      <div className="role-select" aria-label="Account workspace" style={{ marginTop: "6px", marginBottom: "14px" }}>
        {[
          ["citizen", "user", "Citizen", "Missions & EcoPoints"],
          ["farmer", "field", "Farmer", "Sentinel-2 & Parcels"],
          ["nursery", "leaf", "Nursery", "Eco partner store"],
        ].map(([value, icon, title, caption]) => (
          <Button
            key={value}
            kind="text"
            className={role === value ? "active" : ""}
            onClick={() => {
              const r = value as typeof role;
              setRole(r);
              if (tab === "signin") handlePickDemo(r);
            }}
          >
            <Icon name={icon as IconName} />
            <b>{title}</b>
            <span>{caption}</span>
          </Button>
        ))}
      </div>

      {/* One-Click Demo Profiles for Rapid Testing */}
      {tab === "signin" && (
        <div
          style={{
            background: "var(--cream-100, #F4F8F4)",
            border: "1px solid var(--sage-100, #C2D8C6)",
            borderRadius: "16px",
            padding: "12px 14px",
            marginBottom: "16px",
            display: "grid",
            gap: "8px",
          }}
        >
          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
            <span style={{ fontSize: "11px", fontWeight: 800, textTransform: "uppercase", letterSpacing: "0.06em", color: "var(--forest-700)" }}>
              ⚡ 1-Click Demo Profiles:
            </span>
            <span style={{ fontSize: "11px", color: "var(--forest-600)" }}>Tap to switch</span>
          </div>
          <div style={{ display: "grid", gridTemplateColumns: "repeat(3, 1fr)", gap: "6px" }}>
            <button
              type="button"
              onClick={() => handlePickDemo("citizen")}
              style={{
                padding: "8px 6px",
                borderRadius: "10px",
                border: role === "citizen" ? "1.5px solid var(--forest-900)" : "1px solid var(--sage-100)",
                background: role === "citizen" ? "var(--forest-900)" : "#fff",
                color: role === "citizen" ? "#fff" : "var(--forest-900)",
                fontSize: "11px",
                fontWeight: 700,
                cursor: "pointer",
              }}
            >
              🌿 Citizen (Maya)
            </button>
            <button
              type="button"
              onClick={() => handlePickDemo("farmer")}
              style={{
                padding: "8px 6px",
                borderRadius: "10px",
                border: role === "farmer" ? "1.5px solid var(--forest-900)" : "1px solid var(--sage-100)",
                background: role === "farmer" ? "var(--forest-900)" : "#fff",
                color: role === "farmer" ? "#fff" : "var(--forest-900)",
                fontSize: "11px",
                fontWeight: 700,
                cursor: "pointer",
              }}
            >
              🌾 Farmer (Samir)
            </button>
            <button
              type="button"
              onClick={() => handlePickDemo("nursery")}
              style={{
                padding: "8px 6px",
                borderRadius: "10px",
                border: role === "nursery" ? "1.5px solid var(--forest-900)" : "1px solid var(--sage-100)",
                background: role === "nursery" ? "var(--forest-900)" : "#fff",
                color: role === "nursery" ? "#fff" : "var(--forest-900)",
                fontSize: "11px",
                fontWeight: 700,
                cursor: "pointer",
              }}
            >
              🏪 Nursery (Layla)
            </button>
          </div>
        </div>
      )}

      {/* Profile Photo Uploader (Available in Create Account mode) */}
      {tab === "signup" && (
        <div className="photo-uploader">
          <div className="avatar-preview-box">
            {avatar ? (
              <img src={avatar} alt="Profile preview" />
            ) : (
              <Icon name="user" size={30} />
            )}
          </div>
          <div className="photo-uploader-meta">
            <b>Profile Photo</b>
            <small>Upload your own picture (JPG, PNG, WebP)</small>
            <div style={{ display: "flex", gap: "8px", marginTop: "4px" }}>
              <Button
                kind="outline"
                className="compact"
                onClick={() => fileInputRef.current?.click()}
              >
                {avatar ? "Change Photo" : "Upload Photo"}
              </Button>
              {avatar && (
                <button
                  type="button"
                  onClick={() => setAvatar("")}
                  style={{
                    background: "none",
                    border: "none",
                    color: "var(--clay-500, #C25E3B)",
                    fontSize: "11px",
                    fontWeight: 600,
                    cursor: "pointer",
                  }}
                >
                  Remove
                </button>
              )}
            </div>
            <input
              ref={fileInputRef}
              type="file"
              accept="image/*"
              style={{ display: "none" }}
              onChange={handleFileChange}
            />
          </div>
        </div>
      )}

      {errorMsg && (
        <div
          style={{
            background: "#FEE2E2",
            border: "1px solid #F87171",
            color: "#991B1B",
            borderRadius: "12px",
            padding: "10px 14px",
            fontSize: "13px",
            marginBottom: "14px",
            fontWeight: 600,
          }}
        >
          {errorMsg}
        </div>
      )}

      <form onSubmit={submit} noValidate>
        {tab === "signup" && (
          <label>
            Your Full Name
            <input
              value={name}
              onChange={(e) => {
                setName(e.target.value);
                setErrorMsg("");
              }}
              placeholder="e.g. Amina Benali / John Doe"
              autoComplete="name"
              required
            />
          </label>
        )}

        {tab === "signup" && (
          <label>
            {role === "citizen"
              ? "Neighborhood / Community"
              : role === "farmer"
              ? "Farm / Agricultural Holding"
              : "Store / Nursery Name"}
            <input
              value={farmName}
              onChange={(e) => setFarmName(e.target.value)}
              placeholder={
                role === "citizen"
                  ? "e.g. Riverside District"
                  : role === "farmer"
                  ? "e.g. Atlas Olive Grove · Al Haouz"
                  : "e.g. Atlas Eco Garden Supply"
              }
            />
          </label>
        )}

        <label>
          Email Address
          <input
            value={email}
            onChange={(e) => {
              setEmail(e.target.value);
              setErrorMsg("");
            }}
            type="email"
            placeholder={
              role === "citizen"
                ? "citizen@econinhursag.org"
                : role === "farmer"
                ? "farmer@econinhursag.ma"
                : "nursery@econinhursag.ma"
            }
            autoComplete="email"
            required
          />
          {attempted && !emailValid && (
            <small className="field-error">Enter a valid email address.</small>
          )}
        </label>

        <label>
          Password
          <span className="password">
            <input
              value={password}
              onChange={(e) => {
                setPassword(e.target.value);
                setErrorMsg("");
              }}
              type={show ? "text" : "password"}
              placeholder="At least 6 characters"
              autoComplete="current-password"
              required
            />
            <Button kind="text" onClick={() => setShow(!show)}>
              {show ? "Hide" : "Show"}
            </Button>
          </span>
        </label>

        {tab === "signup" && (
          <label className="check" style={{ marginTop: "12px" }}>
            <input
              type="checkbox"
              checked={agreed}
              onChange={(e) => setAgreed(e.target.checked)}
            />
            <i>{agreed && <Icon name="check" size={15} />}</i>
            <span>I accept the <u>Terms of Service</u> and <u>Data Policy</u>.</span>
          </label>
        )}

        <Button
          type="submit"
          className="full"
          style={{ marginTop: "16px" }}
        >
          {tab === "signin"
            ? `Sign In as ${role === "citizen" ? "Citizen" : role === "farmer" ? "Farmer" : "Nursery"}`
            : `Create ${role === "citizen" ? "Citizen" : role === "farmer" ? "Farmer" : "Nursery"} Account`}
        </Button>
      </form>
    </Page>
  );
}

function Home({ go, user, citizen }: { go: (s: Screen) => void; user?: UserProfile; citizen?: ReturnType<typeof useCitizenStore> }) {
  const grown = window.localStorage.getItem("mission-complete") === "true";
  const points = citizen?.profile.points ?? (grown ? 1350 : (user?.points || 1250));
  const [acts, setActs] = useState<string[]>(["Bring a reusable bottle"]);
  const [saved, setSaved] = useState<string[]>([]);
  const [hood, setHood] = useState(0);
  const [tip, setTip] = useState(0);
  const actPool: [string, IconName, string, number][] = [["Bring a reusable bottle", "drop", "Waste", 5], ["Water a street tree", "leaf", "Nature", 10], ["Sort today's recycling", "box", "Waste", 5], ["Walk or cycle one trip", "compass", "Transport", 10], ["Take a 5-minute shower", "drop", "Water", 5], ["Pick up 5 pieces of litter", "heart", "Nature", 10]];
  const [activeActIndices, setActiveActIndices] = useState([0, 1, 2]);
  const dailyActs = activeActIndices.map(i => actPool[i]);
  const earned = dailyActs.filter(a => acts.includes(a[0])).reduce((n, a) => n + a[3], 0);
  const swap = (k: number) => { const used = new Set(activeActIndices); let next = (activeActIndices[k] + 1) % actPool.length; while (used.has(next)) next = (next + 1) % actPool.length; setActiveActIndices(activeActIndices.map((v, j) => j === k ? next : v)); };
  const events: [string, string, string, string][] = [["Seed swap at the library", "Wed · 17:30", "34 going", PHOTO_HANDS], ["Community garden day", "Sat · 09:00", "52 going", PHOTO_PLANTING], ["Park tree walk", "Sun · 11:00", "19 going", PHOTO_GARDEN]];
  const hoodTabs: [string, string, string, string, number][] = [["Air", "Good", "AQI 32", "Cleaner than 78% of city days", 78], ["Green cover", "24%", "+2% this year", "Goal: 30% by 2027", 80], ["Trees", "1,846", "+112 planted", "In your 2 km radius", 62]];
  const tips = ["Unplug chargers when not in use. It saves around 5% on electricity.", "A 5-minute shower uses about 40 litres less water than a bath.", "Leaving grass clippings on the lawn returns nutrients to the soil."];

  const firstName = citizen?.profile.name ? citizen.profile.name.split(" ")[0] : (user?.name ? user.name.split(" ")[0] : "Maya");

  return <Page screen="home" role="citizen" go={go}><div className="content"><Header eyebrow={user?.farmName ? `${user.farmName} · Eco Guardian` : "Citizen Workspace · Eco Guardian"} title={`Good morning,\n${firstName}`} caption="One small action can help an entire place thrive." onBell={() => go("notifications")}/><ContextStrip icon="spark" title="Best time to act" text="Cool morning · 18°C · Ideal for an outdoor mission"/><section className="hero impact impact-clean"><Topo/><div><span className="eyebrow">Your living impact</span><b className="number-xl">{points.toLocaleString()}<small> pts</small></b><p>{grown ? "Your plant grew with today’s mission" : "Complete a mission to help it grow"}</p></div><ImpactPlant grown={grown}/><Pill tone="peach"><Icon name="leaf" size={13}/>{grown ? "Growing Sapling" : "Sapling"}</Pill><div className="level-labels"><span>Sapling</span><span>{grown ? 67 : 62}% to Guardian</span></div><Progress value={grown ? 67 : 62} dark/><footer><button onClick={() => go("explore")}><Icon name="check" size={16}/>{grown ? 25 : 24} missions</button><button onClick={() => go("rewards")}><Icon name="gift" size={16}/>Rewards & partner cafes<Icon name="chevron" size={14}/></button></footer></section><section className="daily-goal"><header><div className="dg-ring" style={{ background: `conic-gradient(var(--peach-200) ${dailyActs.filter(a => acts.includes(a[0])).length / 3 * 360}deg, rgb(255 255 255 / 14%) 0)` }}><span><b>{dailyActs.filter(a => acts.includes(a[0])).length}</b><small>/3</small></span></div><div><span className="eyebrow">Daily goal</span><h2>{dailyActs.every(a => acts.includes(a[0])) ? "All done for today!" : "Three small wins"}</h2><p><b>+{earned}</b> pts earned today</p></div><span className="dg-streak"><Icon name="flame" size={16}/>{citizen?.profile.streakDays || 5}</span></header><div className="dg-week">{["M","T","W","T","F","S","S"].map((d, i) => <span key={i} className={i < 5 ? "done" : i === 5 ? "today" : ""}><i>{i < 5 ? <Icon name="check" size={11}/> : null}</i>{d}</span>)}</div><div className="dg-acts">{dailyActs.map(([t, ic, tag, p], k) => { const on = acts.includes(t); return <div key={t} className={`dg-act ${on ? "on" : ""}`}><button className="dg-main" onClick={() => setActs(on ? acts.filter(x => x !== t) : [...acts, t])}><i className="icon-tile"><Icon name={on ? "check" : ic} size={18}/></i><span><b>{t}</b><small>{tag} · +{p} pts</small></span></button>{!on && <button className="dg-swap" aria-label="Swap action" onClick={() => swap(k)}><Icon name="spark" size={15}/></button>}</div>; })}</div><footer className={dailyActs.every(a => acts.includes(a[0])) ? "unlocked" : ""}><Icon name="gift" size={18}/><span>{dailyActs.every(a => acts.includes(a[0])) ? "Bonus unlocked: +15 pts and a 6-day streak tomorrow" : "Finish all 3 to unlock a +15 pts bonus"}</span></footer></section><div className="quick">{[["map", "Nearby map", "explore"], ["gift", "Rewards", "rewards"], ["cart", "Marketplace", "marketplace"]].map(([icon, label, screen]) => <Button kind="text" key={label} onClick={() => go(screen as Screen)}><i><Icon name={icon as IconName}/></i><span>{label}</span></Button>)}</div><SectionHead title="Happening this week" action="Calendar"/><div className="event-row">{events.map(([t, w, g, img]) => { const on = saved.includes(t); return <article key={t} className="event-card"><img src={img} alt=""/><i className="shade"/><button aria-label="Save event" className={on ? "on" : ""} onClick={() => setSaved(on ? saved.filter(x => x !== t) : [...saved, t])}><Icon name="bookmark" size={16}/></button><div><small>{w}</small><b>{t}</b><span>{on ? "Saved to your week" : g}</span></div></article>; })}</div><SectionHead title="Missions chosen for you" action="See all"/><div className="mission-section-note"><span><Icon name="pin" size={15}/>Closest match · 1.2 km</span><b>Easy to join today</b></div><MissionCard onClick={() => go("mission")}/><section className="card hood-card"><div className="hood-head"><div><span className="eyebrow">Your neighbourhood</span><h2>Riverside district</h2></div></div><div className="hood-tabs">{hoodTabs.map(([n], i) => <button key={n} className={hood === i ? "on" : ""} onClick={() => setHood(i)}>{n}</button>)}</div><div className="hood-body" key={hood}><div><b>{hoodTabs[hood][1]}</b><em>{hoodTabs[hood][2]}</em></div><Progress value={hoodTabs[hood][4]}/><small>{hoodTabs[hood][3]}</small></div></section><section className="tip-card" onClick={() => setTip((tip + 1) % tips.length)}><i className="icon-tile"><Icon name="spark" size={18}/></i><div><span className="eyebrow">Green tip · {tip + 1}/{tips.length}</span><p key={tip}>{tips[tip]}</p><small>Tap for the next tip</small></div></section><section className="card pulse"><span className="live"><i/>Live now</span><Button className="compact" onClick={() => go("community")}>Join</Button><div className="avatars"><i>MC</i><i>JL</i><i>SA</i></div><div><b>18 guardians took action today</b><p>See what your community is growing.</p></div></section></div></Page>;
}

function Explore({ go, citizen }: { go: (s: Screen) => void; citizen?: ReturnType<typeof useCitizenStore> }) {
  const [tab, setTab] = useState<"All" | "Trees" | "Needs Care" | "Missions">("All");
  const [search, setSearch] = useState("");
  const [showLocalize, setShowLocalize] = useState(false);
  const [plantName, setPlantName] = useState("");
  const [species, setSpecies] = useState("Sidr (Ziziphus spina-christi)");
  const [district, setDistrict] = useState("Riverside District · Medina");
  const [lat, setLat] = useState("31.635");
  const [lng, setLng] = useState("-7.990");
  const [notes, setNotes] = useState("");

  const handleDetectGps = () => {
    if (typeof navigator !== "undefined" && navigator.geolocation) {
      navigator.geolocation.getCurrentPosition(
        (pos) => {
          setLat(pos.coords.latitude.toFixed(4));
          setLng(pos.coords.longitude.toFixed(4));
          citizen?.showToast("📍 Current GPS coordinates detected!");
        },
        () => {
          setLat("31.635");
          setLng("-7.990");
          citizen?.showToast("📍 Using Riverside District default GPS.");
        }
      );
    } else {
      setLat("31.635");
      setLng("-7.990");
      citizen?.showToast("📍 Using Riverside District default GPS.");
    }
  };

  const handleCreatePlant = (e: React.FormEvent) => {
    e.preventDefault();
    if (!plantName.trim()) return;
    if (citizen) {
      citizen.localizeNewPlant({
        name: plantName.trim(),
        species,
        district: district.trim() || "Riverside District · Medina",
        lat: parseFloat(lat) || 31.635,
        lng: parseFloat(lng) || -7.990,
        notes: notes.trim(),
      });
      setPlantName("");
      setNotes("");
      setShowLocalize(false);
    }
  };

  const vegList = citizen?.vegetationList || [];
  const selectedPlant = citizen?.selectedVegetation || vegList[0];

  const filtered = vegList.filter((p) => {
    const matchSearch =
      p.name.toLowerCase().includes(search.toLowerCase()) ||
      p.species.toLowerCase().includes(search.toLowerCase()) ||
      p.district.toLowerCase().includes(search.toLowerCase());
    if (!matchSearch) return false;
    if (tab === "Trees") return p.species.toLowerCase().includes("tree") || p.species.toLowerCase().includes("palm") || p.species.toLowerCase().includes("cedar");
    if (tab === "Needs Care") return p.health === "needs-water" || p.health === "attention" || p.health_score < 70;
    if (tab === "Missions") return Boolean(p.mission_title);
    return true;
  });

  return (
    <Page screen="explore" role="citizen" go={go}>
      <TopBar title="Vegetation Map & Explore" action={<Icon name="map" />} actionLabel="Open map" />
      <div className="content top-content">
        <ContextStrip
          icon="pin"
          title="Urban Green Network"
          text={`${vegList.length} localized vegetation sites & active missions in your district`}
        />

        {/* Real Interactive Leaflet Citizen Map */}
        <section className="farm-map" style={{ padding: 0, overflow: "hidden", borderRadius: "20px" }}>
          <header className="map-head" style={{ padding: "12px 14px 6px", display: "flex", justifyContent: "space-between", alignItems: "center" }}>
            <div>
              <h2>Interactive Urban Vegetation Map</h2>
              <small>Tap any localized tree, palm, or garden to inspect health & log care</small>
            </div>
            <div style={{ display: "flex", alignItems: "center", gap: "8px" }}>
              <span><Icon name="leaf" size={13} /> {vegList.length} sites</span>
              <Button kind="primary" className="compact" onClick={() => setShowLocalize(true)}>
                + Localize
              </Button>
            </div>
          </header>
          {vegList.length > 0 && (
            <CitizenMap
              plants={vegList}
              selectedPlantId={citizen?.selectedVegId || null}
              onSelectPlant={(p) => citizen?.setSelectedVegId(p.id)}
              height={380}
            />
          )}
        </section>

        {/* Selected Plant Telemetry & Care Actions */}
        {selectedPlant && (
          <section className="veg-card">
            <div className="veg-head">
              <img src={selectedPlant.photo} alt={selectedPlant.name} />
              <div className="veg-head-meta">
                <span className="eyebrow" style={{ fontSize: "11px", color: "var(--forest-700)" }}>
                  {selectedPlant.district}
                </span>
                <b>{selectedPlant.name}</b>
                <small>
                  {selectedPlant.species} {selectedPlant.species_ar ? `· ${selectedPlant.species_ar}` : ""}
                </small>
                <span style={{ fontSize: "11px", color: "#64748b" }}>
                  📍 {selectedPlant.lat.toFixed(3)}° N, {Math.abs(selectedPlant.lng).toFixed(3)}° W · Planted {selectedPlant.planted_date}
                </span>
              </div>
              <Pill tone={selectedPlant.health === "thriving" ? "sage" : "peach"}>
                {selectedPlant.health === "thriving" ? "Thriving" : "Needs Water"}
              </Pill>
            </div>

            <div className="veg-telemetry-row">
              <div>
                <b>{selectedPlant.health_score}%</b>
                <small>Health Score</small>
              </div>
              <div>
                <b>{selectedPlant.ndvi.toFixed(2)}</b>
                <small>Live S2 NDVI</small>
              </div>
              <div>
                <b>{selectedPlant.last_cared_at}</b>
                <small>Last Checked</small>
              </div>
            </div>

            {selectedPlant.mission_title && (
              <div
                style={{
                  background: "#eff6ff",
                  border: "1px solid #bfdbfe",
                  padding: "8px 12px",
                  borderRadius: "12px",
                  fontSize: "12px",
                  color: "#1e40af",
                  display: "flex",
                  alignItems: "center",
                  gap: "6px",
                }}
              >
                <Icon name="spark" size={15} />
                <span>
                  <b>Active Mission:</b> {selectedPlant.mission_title}
                </span>
              </div>
            )}

            <div className="veg-care-actions">
              <button
                type="button"
                className={`veg-care-btn ${selectedPlant.health === "needs-water" ? "primary" : ""}`}
                onClick={() => citizen?.logCare(selectedPlant.id, "water")}
              >
                💧 Water (+35 pts)
              </button>
              <button
                type="button"
                className="veg-care-btn"
                onClick={() => citizen?.logCare(selectedPlant.id, "clean")}
              >
                🧹 Clean Base (+25 pts)
              </button>
              <button
                type="button"
                className="veg-care-btn"
                onClick={() => citizen?.logCare(selectedPlant.id, "inspect")}
              >
                🔍 Inspect (+25 pts)
              </button>
            </div>
          </section>
        )}

        {/* Search & Category Filter */}
        <div style={{ marginTop: "14px", display: "grid", gap: "10px" }}>
          <div className="search">
            <Icon name="search" size={18} />
            <input
              type="text"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Search vegetation by name, species or district..."
              style={{ border: "none", outline: "none", background: "transparent", width: "100%", fontSize: "13px" }}
            />
          </div>

          <div className="segments">
            {(["All", "Trees", "Needs Care", "Missions"] as const).map((t) => (
              <Button
                key={t}
                className={tab === t ? "active" : ""}
                kind="text"
                onClick={() => setTab(t)}
              >
                {t}
              </Button>
            ))}
          </div>
        </div>

        {/* Localized Plants List */}
        <SectionHead title={`Nearby Localized Vegetation (${filtered.length})`} />
        <div style={{ display: "grid", gap: "10px" }}>
          {filtered.map((plant) => {
            const isSelected = plant.id === selectedPlant?.id;
            return (
              <div
                key={plant.id}
                onClick={() => citizen?.setSelectedVegId(plant.id)}
                style={{
                  display: "flex",
                  alignItems: "center",
                  gap: "12px",
                  background: isSelected ? "var(--cream-100, #F4F8F4)" : "#fff",
                  border: isSelected ? "1.5px solid var(--forest-600, #2E6F40)" : "1px solid var(--sage-100, #C2D8C6)",
                  borderRadius: "16px",
                  padding: "10px 12px",
                  cursor: "pointer",
                  transition: "all 0.15s ease",
                  boxShadow: isSelected ? "0 2px 8px rgba(46, 111, 64, 0.12)" : "none",
                }}
              >
                <img
                  src={plant.photo}
                  alt={plant.name}
                  style={{ width: "48px", height: "48px", borderRadius: "12px", objectFit: "cover" }}
                />
                <div style={{ flex: 1, minWidth: 0, display: "grid", gap: "2px" }}>
                  <b style={{ fontSize: "13px", color: "var(--forest-900, #14281d)" }}>{plant.name}</b>
                  <small style={{ fontSize: "11px", color: "var(--forest-700, #2E6F40)" }}>
                    {plant.species} · 📍 {plant.district}
                  </small>
                  <div style={{ display: "flex", gap: "8px", fontSize: "11px", color: "#64748b" }}>
                    <span>NDVI: <b>{plant.ndvi.toFixed(2)}</b></span>
                    <span>Health: <b>{plant.health_score}%</b></span>
                  </div>
                </div>
                <Pill tone={plant.health === "thriving" ? "sage" : "peach"}>
                  {plant.health === "thriving" ? "Thriving" : "Water"}
                </Pill>
              </div>
            );
          })}
        </div>

        <section className="eco-route" style={{ marginTop: "14px" }}>
          <div className="route-line"><i /><i /><i /></div>
          <div>
            <span className="eyebrow">Suggested green route</span>
            <h2>Two actions, one easy walk</h2>
            <p>Riverside cleanup → Sidr Jujube Tree care</p>
            <footer>
              <span><Icon name="map" size={15} /> 2.8 km</span>
              <span><Icon name="clock" size={15} /> 45 min</span>
              <b>225 pts</b>
            </footer>
          </div>
          <Button kind="icon" label="Open suggested route" onClick={() => go("mission")}>
            <Icon name="chevron" />
          </Button>
        </section>

        <SectionHead title="Active Community Missions" />
        <MissionCard onClick={() => go("mission")} />
        <MissionCard
          title="Plant native shade trees"
          place="North Commons"
          level="Medium"
          points="+150"
          image={PHOTO_PLANTING}
          onClick={() => go("mission")}
        />
        {/* GPS Vegetation Localization Modal in Explore */}
        {showLocalize && (
          <div className="voucher-overlay" onClick={() => setShowLocalize(false)}>
            <div
              className="voucher-modal"
              style={{ maxWidth: "420px", textAlign: "left", gap: "12px", maxHeight: "90vh", overflowY: "auto" }}
              onClick={(e) => e.stopPropagation()}
            >
              <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start" }}>
                <div>
                  <span className="eyebrow" style={{ fontSize: "11px", color: "var(--forest-700)" }}>
                    🌱 GPS Vegetation Localization
                  </span>
                  <h2 style={{ fontSize: "17px", margin: "2px 0 0", color: "var(--forest-900)" }}>
                    Localize a New Plant or Tree
                  </h2>
                </div>
                <button
                  type="button"
                  onClick={() => setShowLocalize(false)}
                  style={{
                    background: "none",
                    border: "none",
                    fontSize: "20px",
                    cursor: "pointer",
                    color: "#64748b",
                    padding: "0 4px",
                  }}
                >
                  ✕
                </button>
              </div>

              <small style={{ color: "#64748b", display: "block" }}>
                Pin new urban trees or gardens to the map to earn <b>+100 EcoPoints</b> immediately.
              </small>

              <form onSubmit={handleCreatePlant} style={{ display: "grid", gap: "10px", marginTop: "4px" }}>
                <label style={{ display: "grid", gap: "4px", fontSize: "12px", fontWeight: 700, color: "var(--forest-900)" }}>
                  Plant Nickname or Tag:
                  <input
                    type="text"
                    required
                    placeholder="e.g. Courtyard Olive Tree #14"
                    value={plantName}
                    onChange={(e) => setPlantName(e.target.value)}
                    style={{
                      padding: "8px 12px",
                      borderRadius: "10px",
                      border: "1px solid var(--sage-100)",
                      fontFamily: "inherit",
                      fontSize: "13px",
                    }}
                  />
                </label>

                <label style={{ display: "grid", gap: "4px", fontSize: "12px", fontWeight: 700, color: "var(--forest-900)" }}>
                  Species:
                  <select
                    value={species}
                    onChange={(e) => setSpecies(e.target.value)}
                    style={{
                      padding: "8px 12px",
                      borderRadius: "10px",
                      border: "1px solid var(--sage-100)",
                      fontFamily: "inherit",
                      fontSize: "13px",
                      background: "#fff",
                    }}
                  >
                    <option value="Sidr (Ziziphus spina-christi)">Sidr (Ziziphus spina-christi)</option>
                    <option value="Date Palm (Phoenix dactylifera)">Date Palm (Phoenix dactylifera)</option>
                    <option value="Olive Tree (Olea europaea)">Olive Tree (Olea europaea)</option>
                    <option value="Bougainvillea spectabilis">Bougainvillea spectabilis</option>
                    <option value="Meyer Lemon (Citrus limon)">Meyer Lemon (Citrus limon)</option>
                    <option value="Atlas Cedar (Cedrus atlantica)">Atlas Cedar (Cedrus atlantica)</option>
                    <option value="Argan Tree (Argania spinosa)">Argan Tree (Argania spinosa)</option>
                    <option value="Pomegranate (Punica granatum)">Pomegranate (Punica granatum)</option>
                  </select>
                </label>

                <div>
                  <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: "4px" }}>
                    <span style={{ fontSize: "12px", fontWeight: 700, color: "var(--forest-900)" }}>
                      GPS Coordinates:
                    </span>
                    <button
                      type="button"
                      onClick={handleDetectGps}
                      style={{
                        background: "none",
                        border: "none",
                        color: "var(--forest-700)",
                        fontSize: "11px",
                        fontWeight: 700,
                        cursor: "pointer",
                        textDecoration: "underline",
                      }}
                    >
                      📍 Detect GPS
                    </button>
                  </div>
                  <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "8px" }}>
                    <input
                      type="text"
                      placeholder="Latitude"
                      value={lat}
                      onChange={(e) => setLat(e.target.value)}
                      style={{
                        padding: "8px 12px",
                        borderRadius: "10px",
                        border: "1px solid var(--sage-100)",
                        fontSize: "13px",
                      }}
                    />
                    <input
                      type="text"
                      placeholder="Longitude"
                      value={lng}
                      onChange={(e) => setLng(e.target.value)}
                      style={{
                        padding: "8px 12px",
                        borderRadius: "10px",
                        border: "1px solid var(--sage-100)",
                        fontSize: "13px",
                      }}
                    />
                  </div>
                </div>

                <label style={{ display: "grid", gap: "4px", fontSize: "12px", fontWeight: 700, color: "var(--forest-900)" }}>
                  District / Neighborhood:
                  <input
                    type="text"
                    placeholder="e.g. Riverside District · Medina"
                    value={district}
                    onChange={(e) => setDistrict(e.target.value)}
                    style={{
                      padding: "8px 12px",
                      borderRadius: "10px",
                      border: "1px solid var(--sage-100)",
                      fontSize: "13px",
                    }}
                  />
                </label>

                <label style={{ display: "grid", gap: "4px", fontSize: "12px", fontWeight: 700, color: "var(--forest-900)" }}>
                  Care Observation Notes:
                  <textarea
                    rows={2}
                    placeholder="e.g. Healthy young sapling, good sunlight, watered today."
                    value={notes}
                    onChange={(e) => setNotes(e.target.value)}
                    style={{
                      padding: "8px 12px",
                      borderRadius: "10px",
                      border: "1px solid var(--sage-100)",
                      fontSize: "13px",
                      fontFamily: "inherit",
                    }}
                  />
                </label>

                <div style={{ display: "flex", gap: "8px", marginTop: "6px" }}>
                  <Button type="button" kind="outline" className="full" onClick={() => setShowLocalize(false)}>
                    Cancel
                  </Button>
                  <Button type="submit" kind="primary" className="full">
                    Save (+100 pts)
                  </Button>
                </div>
              </form>
            </div>
          </div>
        )}
      </div>
    </Page>
  );
}

function Learn({ go }: { go: (s: Screen) => void }) {
  return <Page screen="learn" role="citizen" go={go}><TopBar title="Learn" action={<Icon name="search"/>} actionLabel="Search learning"/><div className="learn-tabs"><b>Featured</b><span>Plant Facts</span><span>Environment</span><span>Guides</span><span>Quizzes</span></div><div className="content"><ContextStrip icon="flame" title="Learning streak · 4 days" text="Finish one lesson today to keep it growing"/><section className="hero learn-hero"><Topo/><Pill tone="peach">Featured · 6 min</Pill><h1>How plants improve urban areas</h1><p>Discover the quiet systems making our streets cooler, cleaner and more alive.</p><Button kind="dark">Read story</Button></section><section className="card quiz-card"><i className="icon-tile peach"><Icon name="spark"/></i><div><h2>Test your eco knowledge</h2><p>5 questions · about 3 min</p></div><span className="leader"><Icon name="flame" size={17}/>You are 3rd on this week's leaderboard</span><Pill tone="mist"><Icon name="spark" size={14}/>50 EcoPoints</Pill><Button>Start quiz</Button></section><SectionHead title="Continue learning"/><div className="lesson"><i><Icon name="leaf"/></i><div><span className="eyebrow">Plant facts</span><b>Native plants, stronger places</b><Progress value={60}/><small>3 of 5 lessons</small></div></div><div className="lesson"><i><Icon name="drop"/></i><div><span className="eyebrow">Environment</span><b>Water-wise everyday habits</b><Progress value={25}/><small>1 of 4 lessons</small></div></div></div></Page>;
}

function Community({ go }: { go: (s: Screen) => void }) {
  const [claimed, setClaimed] = useState<string[]>([]);
  const [liked, setLiked] = useState(false);
  const [spot, setSpot] = useState<number | null>(null);
  const takenSpots = new Set([0,1,2,4,5,7,8,9,10,12,13,14,16,17,18,19,21,22,24,25,26,28,29,31,33,34,36,37,40,41,43,44,46,48,49,51,52,55,56,57,59,38,30]);
  const [joined, setJoined] = useState<string[]>(["Plant Clinic"]);
  const [filter, setFilter] = useState("All");
  const chMeta: Record<string, [string, string, string[], number]> = { "Plant Clinic": ["Plant questions and practical answers", "Most active", ["MY", "JL", "SA"], 3], "Tree Crew": ["Planting days and tree care", "Events", ["JL", "AK"], 1], "Local Action": ["Cleanups and street projects", "Events", ["SA", "MC", "RB"], 5], "Water Wise": ["Save water at home and garden", "Tips", ["AL", "MY"], 0] };
  const swaps = [["leaf", "Tomato seedlings", "Maya · 0.4 km"], ["drop", "Rain barrel", "Jon · 0.9 km"], ["gift", "Compost bags", "Sara · 1.1 km"], ["leaf", "Herb cuttings", "Ali · 1.5 km"]];
  const ranks = [["1", "SA", "Sara A.", "1,240"], ["2", "JL", "Jon L.", "1,105"], ["3", "MC", "You", "980"], ["4", "AL", "Ali K.", "860"]];
  const channels = [["leaf", "Plant Clinic", "34", "Maya", "My basil is thriving again.", "sage"], ["leaf", "Tree Crew", "21", "Jon", "Saturday’s planting site is ready.", "peach"], ["people", "Local Action", "48", "Sara", "Who is joining Riverside cleanup?", "mist"], ["drop", "Water Wise", "16", "Ali", "Early watering saved a third of my usage.", "sage"]];
  const shown = channels.filter(c => filter === "All" || (filter === "Joined" ? joined.includes(c[1]) : chMeta[c[1]][1] === filter));
  return <Page screen="community" role="citizen" go={go}><TopBar title="Eco Community"/><div className="content top-content"><ContextStrip icon="chat" title="Conversation of the day" text="What is your best water-saving habit?"/><section className="hero community-hero"><Topo/><span className="live"><i/>18 guardians active now</span><h1>Share what works. Grow local change.</h1><p>Small local knowledge becomes a stronger neighbourhood.</p></section><section className="community-compose"><span className="big-avatar">MC</span><Button kind="text"><span>Share a question, tip or local win…</span><Icon name="plus" size={18}/></Button></section><div className="community-actions"><Button kind="text"><Icon name="chat" size={18}/><span>Ask for help</span></Button><Button kind="text"><Icon name="gift" size={18}/><span>Offer supplies</span></Button><Button kind="text"><Icon name="people" size={18}/><span>Start a crew</span></Button></div><section className="community-spotlight"><img src={PHOTO_PARK} alt="A local gardener caring for a green neighborhood space"/><i/><div><Pill tone="peach">Neighbourhood goal</Pill><h2>One block, 60 native plants</h2><p>{spot !== null ? "You joined 43 neighbours growing a cooler street." : "43 neighbours have already pledged a plant."}</p></div></section><section className="block-goal"><header><div><span className="eyebrow">Green the block · 7 days left</span><h2>Claim your spot on Elm Street</h2></div><b>{43 + (spot !== null ? 1 : 0)}<small>/60</small></b></header><div className="street"><div className="street-row">{Array.from({ length: 30 }, (_, i) => i).map(i => <button key={i} aria-label={`Spot ${i + 1}`} disabled={takenSpots.has(i)} className={takenSpots.has(i) ? "taken" : spot === i ? "mine" : ""} onClick={() => setSpot(spot === i ? null : i)}>{(takenSpots.has(i) || spot === i) && <Icon name="leaf" size={12}/>}</button>)}</div><div className="road"><span/></div><div className="street-row">{Array.from({ length: 30 }, (_, i) => i + 30).map(i => <button key={i} aria-label={`Spot ${i + 1}`} disabled={takenSpots.has(i)} className={takenSpots.has(i) ? "taken" : spot === i ? "mine" : ""} onClick={() => setSpot(spot === i ? null : i)}>{(takenSpots.has(i) || spot === i) && <Icon name="leaf" size={12}/>}</button>)}</div></div><div className="street-legend"><span><i className="taken"/>Pledged</span><span><i/>Open</span><span><i className="mine"/>Yours</span></div><p className="block-note">{spot === null ? "Tap any open spot to plant in front of a real address on your street." : <>Spot <b>#{spot + 1}</b> is yours — {spot < 30 ? "north" : "south"} side, near no. {(spot % 30) * 2 + 4}. We'll bring a native seedling on Saturday.</>}</p><footer><span className="goal-avatars"><i>MC</i><i>JL</i><i>SA</i><b>+40</b></span>{spot !== null ? <Button className="compact" kind="outline" onClick={() => setSpot(null)}>Release spot</Button> : <span className="block-hint">17 spots open</span>}</footer></section><section className="community-event"><i className="icon-tile"><Icon name="people"/></i><div><span className="eyebrow">This Saturday</span><h2>Riverside action circle</h2><p>12 neighbours are planning together.</p></div><Button className="compact" onClick={() => go("mission")}>View</Button></section><div className="community-impact"><div><b>312</b><small>trees planted this month</small></div><div><b>1.8t</b><small>waste collected</small></div><div><b>46k L</b><small>water saved</small></div></div><SectionHead title="Neighbour swap shelf"/><div className="swap-row">{swaps.map(([icon, title, who]) => { const done = claimed.includes(title); return <div className="swap-card" key={title}><span className="icon-tile"><Icon name={icon as IconName}/></span><b>{title}</b><small>Free · {who}</small><Button className="compact" kind={done ? "outline" : "primary"} onClick={() => setClaimed(done ? claimed.filter(t => t !== title) : [...claimed, title])}>{done ? "Requested" : "Request"}</Button></div>; })}</div><SectionHead title="Top guardians this week"/><ol className="leaderboard">{ranks.map(([n, av, name, pts]) => <li key={n} className={name === "You" ? "me" : ""}><span>{n}</span><span className="rank-av">{av}</span><b>{name}</b><em>{pts} pts</em></li>)}</ol><section className="community-tip"><span className="eyebrow">Tip of the day · from Ali</span><p>“Water at dawn. Less evaporation means up to a third less water for the same healthy garden.”</p><footer><span>{liked ? "57" : "56"} neighbours found this useful</span><Button className="compact" kind={liked ? "primary" : "outline"} onClick={() => setLiked(!liked)}>{liked ? "Thanks!" : "Helpful"}</Button></footer></section><SectionHead title="Community channels"/><div className="channel-filters">{["All", "Joined", "Events", "Tips"].map(f => <button key={f} className={filter === f ? "on" : ""} onClick={() => setFilter(f)}>{f}{f === "Joined" ? ` · ${joined.length}` : ""}</button>)}</div><div className="channel-list">{shown.length === 0 && <p className="channel-empty">No channels here yet. Try another filter.</p>}{shown.map(([icon, title, count, name, msg, tone]) => { const [desc, tag, avs, unread] = chMeta[title]; const isIn = joined.includes(title); return <article className={`channel-card ${tone}`} key={title}><header><span className="icon-tile"><Icon name={icon as IconName}/></span><div><b>{title}</b><small>{desc}</small></div>{isIn && unread > 0 && <span className="unread">{unread}</span>}</header><div className="channel-msg"><span className="rank-av">{name.slice(0, 2).toUpperCase()}</span><p><b>{name}</b> {msg}</p><i>2m</i></div><footer><span className="goal-avatars">{avs.map(a => <i key={a}>{a}</i>)}</span><span className="online"><i/>{count} online · {tag}</span><Button className="compact" kind={isIn ? "outline" : "primary"} onClick={() => setJoined(isIn ? joined.filter(j => j !== title) : [...joined, title])}>{isIn ? "Joined" : "Join"}</Button></footer></article>; })}<Button kind="text" className="channel-new"><Icon name="plus" size={18}/><span>Suggest a new channel</span></Button></div></div></Page>;
}

function ProfileCover({
  photo,
  initials,
  name,
  sub,
  tag,
  square = false,
  avatarPhoto,
  onUploadAvatar,
}: {
  photo: string;
  initials: string;
  name: string;
  sub: string;
  tag: string;
  square?: boolean;
  avatarPhoto?: string;
  onUploadAvatar?: (url: string) => void;
}) {
  const fileRef = useRef<HTMLInputElement>(null);

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file && onUploadAvatar) {
      const reader = new FileReader();
      reader.onload = () => {
        if (typeof reader.result === "string") {
          onUploadAvatar(reader.result);
        }
      };
      reader.readAsDataURL(file);
    }
  };

  return (
    <section className="profile-cover">
      <div className="cover-photo">
        <img src={photo} alt="" />
        <i />
      </div>
      <div className="cover-body">
        <div className="cover-top-row">
          <div className="cover-av-wrap" style={{ position: "relative", width: "fit-content" }}>
            <span
              className={square ? "business-logo cover-av" : "big-avatar cover-av"}
              style={{
                overflow: "hidden",
                position: "relative",
                cursor: onUploadAvatar ? "pointer" : "default",
              }}
              onClick={() => onUploadAvatar && fileRef.current?.click()}
              title={onUploadAvatar ? "Click to change photo" : undefined}
            >
              {avatarPhoto ? (
                <img
                  src={avatarPhoto}
                  alt={name}
                  style={{
                    width: "100%",
                    height: "100%",
                    objectFit: "cover",
                    borderRadius: square ? "16px" : "50%",
                  }}
                />
              ) : (
                initials
              )}
              <i><Icon name="check" size={13} /></i>
            </span>
            {onUploadAvatar && (
              <input
                ref={fileRef}
                type="file"
                accept="image/*"
                style={{ display: "none" }}
                onChange={handleFileChange}
              />
            )}
          </div>
          <Pill tone="peach">{tag}</Pill>
        </div>
        <div className="cover-text">
          <h1>{name}</h1>
          <p>{sub}</p>
          {onUploadAvatar && (
            <button
              type="button"
              onClick={() => fileRef.current?.click()}
              style={{
                background: "transparent",
                border: "none",
                color: "var(--forest-700, #1F4B2C)",
                fontSize: "11px",
                fontWeight: 700,
                cursor: "pointer",
                padding: "2px 0",
                textDecoration: "underline",
                width: "fit-content",
              }}
            >
              📷 Change photo
            </button>
          )}
        </div>
      </div>
    </section>
  );
}

function PrefToggles({
  items,
  onToggle,
}: {
  items: [string, string, boolean][];
  onToggle?: (index: number, val: boolean) => void;
}) {
  const [on, setOn] = useState(items.map((i) => i[2]));
  useEffect(() => {
    setOn(items.map((i) => i[2]));
  }, [items]);
  return (
    <section className="pref-card">
      {items.map(([t, d], i) => (
        <button
          key={t}
          type="button"
          onClick={() => {
            const next = !on[i];
            setOn(on.map((v, j) => (j === i ? next : v)));
            onToggle?.(i, next);
          }}
        >
          <span>
            <b>{t}</b>
            <small>{d}</small>
          </span>
          <i className={`switch ${on[i] ? "on" : ""}`} />
        </button>
      ))}
    </section>
  );
}

function LogoutButton({ go }: { go: (s: Screen) => void }) {
  const { signOut } = useAuth();
  return (
    <Button
      kind="outline"
      onClick={async () => {
        try {
          await signOut();
        } catch {}
        window.sessionStorage.removeItem("eco-authenticated");
        go("welcome");
      }}
    >
      Log out
    </Button>
  );
}

function LogoutRow({ go, who }: { go: (s: Screen) => void; who: string }) {
  return (
    <section className="account-actions">
      <div><b>Account</b><span>Signed in as {who}</span></div>
      {CLERK_PUBLISHABLE_KEY ? (
        <LogoutButton go={go} />
      ) : (
        <Button
          kind="outline"
          onClick={() => {
            window.sessionStorage.removeItem("eco-authenticated");
            window.sessionStorage.removeItem("eco-user");
            go("signup");
          }}
        >
          Log out
        </Button>
      )}
    </section>
  );
}

function Profile({
  go,
  user,
  citizen,
  onUpdateAvatar,
}: {
  go: (s: Screen) => void;
  user?: UserProfile;
  citizen?: ReturnType<typeof useCitizenStore>;
  onUpdateAvatar?: (url: string) => void;
}) {
  const profileData = citizen?.profile;
  const name = profileData?.name || user?.name || "Maya Chen";
  const initials = name
    .split(" ")
    .map((w) => w[0])
    .slice(0, 2)
    .join("")
    .toUpperCase();
  const sub = user?.farmName ? `${user.farmName} · Member since 2024` : "Riverside District · Member since 2024";

  const [tab, setTab] = useState<"wallet" | "settings">("wallet");

  const goal = profileData?.monthlyGoal || 4;
  const points = profileData?.points ?? (user?.points || 1250);
  const completedMissions = profileData?.completedMissions || 24;
  const claimedVouchers = citizen?.claimedVouchers || [];

  const copyVoucherCode = (code: string) => {
    if (typeof navigator !== "undefined" && navigator.clipboard) {
      navigator.clipboard.writeText(code).catch(() => {});
    }
    citizen?.showToast(`Copied voucher code ${code}! 📋`);
  };

  const ledgerItems = [
    { id: "1", title: "Completed 'Restore Riverside Park' Mission", date: "Today · 14:20", pts: "+100", type: "plus", icon: "check" as IconName },
    { id: "2", title: "Watered Urban Sidr Tree #102", date: "Yesterday · 09:15", pts: "+35", type: "plus", icon: "drop" as IconName },
    { id: "3", title: "Redeemed Organic Mint Tea at Café des Oliviers", date: "Oct 8, 2026", pts: "-250", type: "minus", icon: "gift" as IconName },
    { id: "4", title: "Weeded Medina Pocket Garden Base", date: "Oct 7, 2026", pts: "+25", type: "plus", icon: "leaf" as IconName },
    { id: "5", title: "Daily 3-Win Streak Bonus Earned", date: "Oct 6, 2026", pts: "+15", type: "plus", icon: "flame" as IconName },
    { id: "6", title: "Passed Urban Biodiversity Knowledge Quiz", date: "Oct 4, 2026", pts: "+50", type: "plus", icon: "spark" as IconName },
  ];

  return (
    <Page screen="profile" role="citizen" go={go}>
      <TopBar title="Citizen Profile" action={<Icon name="gear" />} user={user} onLogout={() => go("signup")} />
      <div className="content top-content profile">
        {/* Cover Photo & Avatar Header */}
        <ProfileCover
          photo={PHOTO_PARK}
          initials={initials}
          name={name}
          sub={sub}
          tag="Sapling"
          avatarPhoto={user?.avatar}
          onUploadAvatar={onUpdateAvatar}
        />

        {/* Quick Metrics Bar */}
        <Stats
          items={[
            [`${points.toLocaleString()}`, "EcoPoints"],
            [`${completedMissions}`, "Missions"],
            [`${profileData?.streakDays || 5}d`, "Active Streak"],
          ]}
        />

        {/* Segmented Two-Tab Navigation */}
        <div className="profile-two-tabs">
          <button
            type="button"
            className={`profile-tab-pill ${tab === "wallet" ? "active" : ""}`}
            onClick={() => setTab("wallet")}
          >
            <Icon name="gift" size={16} />
            <span>Eco Wallet & Passes</span>
          </button>
          <button
            type="button"
            className={`profile-tab-pill ${tab === "settings" ? "active" : ""}`}
            onClick={() => setTab("settings")}
          >
            <Icon name="gear" size={16} />
            <span>Settings & Account</span>
          </button>
        </div>

        {/* ================= TAB 1: ECO WALLET & PASSES ================= */}
        {tab === "wallet" && (
          <div style={{ display: "grid", gap: "16px" }}>
            {/* Digital Eco-Wallet Card */}
            <section className="wallet-hero-card">
              <div className="wallet-hero-head">
                <div>
                  <span className="eyebrow">DIGITAL ECO-WALLET · VERIFIED CITIZEN</span>
                  <div className="wallet-balance-num">
                    {points.toLocaleString()}
                    <small>EcoPoints</small>
                  </div>
                </div>
                <span className="wallet-tier-pill">
                  <Icon name="leaf" size={13} /> Sapling Tier
                </span>
              </div>

              <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", fontSize: "12px", color: "var(--peach-200)" }}>
                <span>Level Progress: 62% to Guardian</span>
                <span>Best Streak: 12 days</span>
              </div>

              <div className="wallet-quick-actions">
                <Button
                  className="full"
                  kind="primary"
                  onClick={() => go("rewards")}
                  style={{ background: "#fbbf24", color: "#14281d", fontWeight: 800 }}
                >
                  🎁 Spend Points at Partner Shops
                </Button>
              </div>
            </section>

            {/* Active Vouchers & Partner Passes */}
            <SectionHead title={`Claimed Vouchers (${claimedVouchers.length})`} />
            {claimedVouchers.length === 0 ? (
              <div
                style={{
                  background: "#ffffff",
                  borderRadius: "22px",
                  padding: "24px 20px",
                  textAlign: "center",
                  border: "1px dashed var(--sage-100, #C2D8C6)",
                  display: "grid",
                  gap: "10px",
                  placeItems: "center",
                }}
              >
                <div style={{ width: "48px", height: "48px", borderRadius: "14px", background: "var(--cream-100)", display: "grid", placeItems: "center" }}>
                  <Icon name="gift" size={24} />
                </div>
                <div>
                  <b style={{ display: "block", fontSize: "15px", color: "var(--forest-900)" }}>
                    No Active Voucher Passes Yet
                  </b>
                  <p style={{ fontSize: "12px", color: "#64748b", margin: "4px 0 0" }}>
                    Redeem your EcoPoints for artisan coffees, organic teas, or sustainable clothing vouchers.
                  </p>
                </div>
                <Button kind="outline" className="compact" onClick={() => go("rewards")}>
                  Explore Partner Offers
                </Button>
              </div>
            ) : (
              <div style={{ display: "grid", gap: "10px" }}>
                {claimedVouchers.map((v) => (
                  <div key={v.id} className="voucher-pass-card">
                    <div className="voucher-pass-header">
                      <div>
                        <b>{v.offerTitle}</b>
                        <small>{v.partner} · Claimed {v.claimedAt}</small>
                      </div>
                      <Pill tone="sage">Verified Pass</Pill>
                    </div>

                    <div className="voucher-code-strip">
                      <code>{v.code}</code>
                      <Button kind="outline" className="compact" onClick={() => copyVoucherCode(v.code)}>
                        Copy
                      </Button>
                    </div>

                    <div style={{ fontSize: "16px", letterSpacing: "5px", color: "#94a3b8", textAlign: "center" }}>
                      |||| | || |||| | ||| ||||
                    </div>
                  </div>
                ))}
              </div>
            )}

            {/* Points Activity Ledger */}
            <SectionHead title="EcoPoints Activity History" />
            <div className="ledger-list">
              {ledgerItems.map((item) => (
                <div key={item.id} className="ledger-item">
                  <div className="ledger-item-left">
                    <span className={`ledger-icon ${item.type}`}>
                      <Icon name={item.icon} size={16} />
                    </span>
                    <div className="ledger-meta">
                      <b>{item.title}</b>
                      <small>{item.date}</small>
                    </div>
                  </div>
                  <span className={`ledger-pts ${item.type}`}>
                    {item.pts}
                  </span>
                </div>
              ))}
            </div>

            {/* Level Milestones Progress Card */}
            <section className="card level" style={{ marginTop: 0 }}>
              <div>
                <h2>Level Milestone</h2>
                <Pill tone="peach">Sapling (Level 2)</Pill>
              </div>
              <div className="level-track">
                <span>Seedling</span>
                <b><i /></b>
                <span>Sapling</span>
                <b className="active"><i /></b>
                <span>Guardian</span>
              </div>
              <p style={{ fontSize: "12px", color: "#64748b", margin: "14px 0 0" }}>
                Earn <b>750 more EcoPoints</b> through community missions to reach the prestigious <b>Guardian Tier</b>.
              </p>
            </section>
          </div>
        )}

        {/* ================= TAB 2: SETTINGS & ACCOUNT ================= */}
        {tab === "settings" && (
          <div style={{ display: "grid", gap: "16px" }}>
            {/* Account Details Card */}
            <SectionHead title="Citizen Account Information" />
            <div className="profile-account-card">
              <div className="profile-account-row">
                <span>Full Name</span>
                <b>{name}</b>
              </div>
              <div className="profile-account-row">
                <span>Account Role</span>
                <Pill tone="sage">Verified Eco Citizen</Pill>
              </div>
              <div className="profile-account-row">
                <span>Email Address</span>
                <b>{profileData?.email || user?.email || "citizen@econinhursag.ma"}</b>
              </div>
              <div className="profile-account-row">
                <span>Home District</span>
                <b>Riverside District · Al Haouz</b>
              </div>
              <div className="profile-account-row">
                <span>Member Since</span>
                <b>March 2024</b>
              </div>
            </div>

            {/* Notification Preferences */}
            <SectionHead title="Notification Preferences" />
            <PrefToggles
              items={[
                [
                  "Mission Reminders",
                  "A nudge the day before you volunteer",
                  profileData?.notificationPrefs.reminders ?? true,
                ],
                [
                  "Nearby Mission Alerts",
                  "Instant alerts within 3 km of your home",
                  profileData?.notificationPrefs.nearby_missions ?? true,
                ],
                [
                  "Weekly Impact Digest",
                  "A personalized eco summary every Sunday",
                  profileData?.notificationPrefs.weekly_email ?? false,
                ],
              ]}
              onToggle={(idx, val) => {
                const keys: ("reminders" | "nearby_missions" | "weekly_email")[] = [
                  "reminders",
                  "nearby_missions",
                  "weekly_email",
                ];
                citizen?.updatePrefs(keys[idx], val);
              }}
            />

            {/* Monthly Mission Goal Setting */}
            <section className="card goal-card" style={{ marginTop: 0 }}>
              <div>
                <h2>Monthly Mission Target</h2>
                <small>
                  {Math.min(completedMissions, goal)} of {goal} completed ·{" "}
                  {goal > completedMissions ? `${goal - completedMissions} missions to goal` : "goal reached"}
                </small>
              </div>
              <div className="goal-picker">
                {[2, 4, 6, 8].map((n) => (
                  <button
                    key={n}
                    type="button"
                    className={goal === n ? "on" : ""}
                    onClick={() => citizen?.updateMonthlyGoal(n)}
                  >
                    {n}
                  </button>
                ))}
              </div>
              <Progress value={Math.min(100, (completedMissions / goal) * 100)} />
            </section>

            {/* App Preferences */}
            <SectionHead title="App Preferences" />
            <div className="profile-account-card">
              <div className="profile-account-row">
                <span>Interface Language</span>
                <b>English (US)</b>
              </div>
              <div className="profile-account-row">
                <span>Measurement Units</span>
                <b>Metric (km, kg, L)</b>
              </div>
              <div className="profile-account-row">
                <span>Data Privacy</span>
                <span style={{ color: "var(--forest-700)", fontWeight: 700, cursor: "pointer", textDecoration: "underline" }}>
                  Download My Data
                </span>
              </div>
            </div>

            {/* Session Management & Logout */}
            <LogoutRow go={go} who={name} />
          </div>
        )}
      </div>
    </Page>
  );
}

function Marketplace({ go }: { go: (s: Screen) => void }) {
  const [tab, setTab] = useState<"Products" | "Stores">("Products");
  const [q, setQ] = useState("");
  const [cart, setCart] = useState<string[]>([]);
  const stores: [string, string, string, string, string][] = [
    ["Café des Oliviers & Eco-Boutique", "Organic coffees, teas, Moroccan olive oil", "0.4 km", "4.9", PHOTO_STORE],
    ["Atlas Threads Sustainable Apparel", "Organic cotton clothing & canvas bags", "0.9 km", "4.8", PHOTO_HANDS],
    ["Green Garden Nursery & Seed Co.", "Native plants · Peat-free soil", "1.2 km", "4.8", PHOTO_STORE],
    ["Riverside Seed Co-op", "Heirloom seeds · Seed swaps", "1.8 km", "4.7", PHOTO_GREENHOUSE],
  ];
  const products: [string, string, string, string][] = [
    ["Organic Cotton Eco-Tote", "Atlas Threads Sustainable Apparel", "$15.00", PHOTO_HANDS],
    ["Native Moroccan Herb Collection", "Green Garden Nursery", "$24.00", PHOTO_PRODUCT],
    ["Cold Brew Organic Coffee (1L)", "Café des Oliviers Partner", "$8.50", PHOTO_STORE],
    ["Water-wise Indoor Plant", "Green Garden Nursery", "$18.50", PHOTO_GREENHOUSE],
    ["Heirloom Tomato Seeds", "Riverside Seed Co-op", "$4.50", PHOTO_PLANTING],
    ["Community Bio-Compost 10 L", "Green Garden Nursery", "$9.00", PHOTO_HANDS],
  ];
  const m = (t: string) => t.toLowerCase().includes(q.toLowerCase());
  const shownS = stores.filter(x => m(x[0]) || m(x[1]));
  const shownP = products.filter(x => m(x[0]) || m(x[1]));
  return <Page screen="marketplace" go={go}><DetailTop title="Eco Marketplace" back="home" go={go}/><div className="content detail-content mk"><ContextStrip icon="leaf" title="Shop local, verified eco" text={cart.length ? `${cart.length} item${cart.length > 1 ? "s" : ""} in your basket · pickup nearby` : "Earn EcoPoints on every purchase"}/><label className="search"><Icon name="search" size={18}/><input value={q} onChange={e => setQ(e.target.value)} placeholder="Search plants, apparel, seeds, cafés"/></label><div className="segments">{(["Products", "Stores"] as const).map(x => <Button key={x} kind="text" className={tab === x ? "active" : ""} onClick={() => setTab(x)}>{x}</Button>)}</div>
{tab === "Products" ? <div className="mk-grid">{shownP.length === 0 && <p className="channel-empty">No products match.</p>}{shownP.map(([n, st, pr, img]) => { const on = cart.includes(n); return <section key={n} className="mk-product"><img src={img} alt=""/><div><b>{n}</b><small>{st}</small><footer><span>{pr}</span><button className={on ? "on" : ""} aria-label={on ? "Remove from basket" : "Add to basket"} onClick={() => setCart(on ? cart.filter(x => x !== n) : [...cart, n])}><Icon name={on ? "check" : "plus"} size={16}/></button></footer></div></section>; })}</div>
: <div className="mk-stores">{shownS.length === 0 && <p className="channel-empty">No stores match.</p>}{shownS.map(([n, d, km, r, img]) => <section key={n} className="mk-store"><img src={img} alt=""/><div><b>{n}</b><small>{d}</small><span><Icon name="pin" size={13}/>{km} · ★ {r}</span></div><Pill tone="sage">Eco</Pill></section>)}</div>}
</div></Page>;
}

function DetailTop({ title, back, go }: { title: string; back: Screen; go: (s: Screen) => void }) {
  return <header className="topbar detail-top"><Button kind="icon" label="Back" onClick={() => go(back)}><Icon name="back"/></Button><strong>{title}</strong><Logo/></header>;
}

function MissionDetail({ go, citizen }: { go: (s: Screen) => void; citizen?: ReturnType<typeof useCitizenStore> }) {
  const [joined, setJoined] = useState(false);
  const [complete, setComplete] = useState(false);
  if (complete) return <Page screen="mission" go={go} className="celebration"><div className="leaf-confetti">{[1,2,3,4,5,6].map(i => <i key={i}/>)}</div><span className="success-mark"><Icon name="check" size={38}/></span><span className="eyebrow">Mission complete</span><h1>Riverside is greener because of you.</h1><p>Your action has been verified and added to your impact.</p><section className="reward-earned"><Icon name="spark"/><div><b>+100 EcoPoints</b><span>New total · {citizen?.profile.points.toLocaleString() || "1,350"}</span></div></section><Button className="full" onClick={() => go("home")}>See my impact</Button><Button kind="text" onClick={() => go("rewards")}>Explore rewards</Button></Page>;
  return <Page screen="mission" go={go}><DetailTop title="Mission details" back="home" go={go}/><div className="detail-photo"><img src={PHOTO_PARK} alt="Gardener caring for Riverside Park"/><i/><Pill>Easy</Pill></div><div className="content detail-content"><span className="eyebrow">Riverside Park · 1.2 km</span><h1>Restore Riverside Park</h1><p>Help remove litter, refresh planting beds and make this shared green space welcoming again.</p><div className="detail-facts"><span><Icon name="clock"/><b>Saturday</b><small>10:00–12:00</small></span><span><Icon name="people"/><b>18 joined</b><small>4 spots left</small></span><span><Icon name="gift"/><b>100 points</b><small>On completion</small></span></div><section className="card organizer"><span className="big-avatar">RP</span><div><small>Hosted by</small><h2>Riverside Park Friends</h2><p>Verified community partner</p></div><Icon name="shield"/></section><SectionHead title="What you'll do"/><div className="task-list"><span><Icon name="check"/>Collect and sort visible litter</span><span><Icon name="check"/>Refresh two native planting beds</span><span><Icon name="check"/>Log the area's final condition</span></div>{joined ? <section className="joined-card"><Icon name="check"/><div><b>You're joining this mission</b><span>We'll remind you Saturday morning.</span></div><Button onClick={() => { window.localStorage.setItem("mission-complete", "true"); citizen?.completeMission("Restore Riverside Park", 100); setComplete(true); }}>Complete</Button></section> : <Button className="full mission-join" onClick={() => setJoined(true)}>Join mission · Free</Button>}</div></Page>;
}

function Rewards({
  go,
  user,
  citizen,
}: {
  go: (s: Screen) => void;
  user?: UserProfile;
  citizen?: ReturnType<typeof useCitizenStore>;
}) {
  const [tab, setTab] = useState<"All" | "Cafés" | "Clothing" | "Claimed">("All");
  const [activeVoucher, setActiveVoucher] = useState<{
    code: string;
    partner: string;
    offerTitle: string;
    pointsCost: number;
  } | null>(null);

  const balance = citizen?.profile.points ?? (user?.points || 1250);
  const claimedVouchers = citizen?.claimedVouchers || [];

  const offers: [string, string, number, string, IconName][] = [
    ["Organic Mint Tea & Moroccan Pastry", "Café des Oliviers · Partner Coffee Shop", 250, "cafes", "gift"],
    ["Specialty Single-Origin Cold Brew", "Medina Artisan Roasters Partner", 350, "cafes", "gift"],
    ["100% Organic Cotton Eco-Tote", "Atlas Threads Sustainable Apparel", 400, "clothing", "gift"],
    ["$15 Sustainable Clothing Voucher", "Atlas Threads Sustainable Apparel", 500, "clothing", "gift"],
    ["25% Voucher on Eco-Apparel", "Marrakech Eco-Wear Co. Partner", 450, "clothing", "gift"],
    ["Native Moroccan Seedling (3pk)", "Green Garden Nursery Partner", 300, "clothing", "leaf"],
    ["Peat-Free Organic Compost 15L", "Green Garden Nursery Partner", 450, "clothing", "leaf"],
  ];

  const filteredOffers = offers.filter(([, , , cat]) => {
    if (tab === "Cafés") return cat === "cafes";
    if (tab === "Clothing") return cat === "clothing";
    return true;
  });

  const handleRedeem = async (title: string, partner: string, cost: number) => {
    if (!citizen) return;
    const v = await citizen.redeemReward(title, partner, title, cost);
    if (v) {
      setActiveVoucher(v);
    }
  };

  const copyCode = (code: string) => {
    if (typeof navigator !== "undefined" && navigator.clipboard) {
      navigator.clipboard.writeText(code).catch(() => {});
    }
    if (citizen) {
      citizen.showToast(`Copied voucher code ${code}! 📋`);
    }
  };

  return (
    <Page screen="rewards" go={go}>
      <DetailTop title="Partner Rewards & EcoPoints" back="home" go={go} />
      <div className="content top-content">
        <section className="hero reward-balance">
          <Topo />
          <span className="eyebrow">Available EcoPoints</span>
          <b>{balance.toLocaleString()}</b>
          <p>Redeemable at verified partner coffee shops & sustainable clothing stores</p>
        </section>

        {/* Voucher Modal */}
        {activeVoucher && (
          <div className="voucher-overlay" onClick={() => setActiveVoucher(null)}>
            <div className="voucher-modal" onClick={(e) => e.stopPropagation()}>
              <span className="eyebrow" style={{ fontSize: "11px", color: "var(--forest-700)" }}>
                {activeVoucher.partner}
              </span>
              <h2 style={{ fontSize: "18px", margin: 0, color: "var(--forest-900)" }}>
                {activeVoucher.offerTitle}
              </h2>
              <Pill tone="sage">Verified Eco Voucher</Pill>

              <div className="voucher-code-box">
                <small style={{ color: "#64748b" }}>Present this voucher at checkout:</small>
                <b>{activeVoucher.code}</b>
                <div style={{ fontSize: "20px", letterSpacing: "4px", color: "#64748b", margin: "4px 0" }}>
                  ||| | |||| | || |||| | |||
                </div>
              </div>

              <p style={{ fontSize: "12px", color: "#64748b", margin: 0 }}>
                Valid for 30 days at all participating partner locations.
              </p>

              <div style={{ display: "flex", gap: "8px" }}>
                <Button className="full" kind="outline" onClick={() => copyCode(activeVoucher.code)}>
                  Copy Code
                </Button>
                <Button className="full" onClick={() => setActiveVoucher(null)}>
                  Done
                </Button>
              </div>
            </div>
          </div>
        )}

        <div className="reward-tabs">
          {(["All", "Cafés", "Clothing", "Claimed"] as const).map((t) => (
            <Button
              key={t}
              kind="text"
              className={tab === t ? "active" : ""}
              onClick={() => setTab(t)}
            >
              {t === "Claimed" ? `Claimed (${claimedVouchers.length})` : t}
            </Button>
          ))}
        </div>

        {tab === "Claimed" ? (
          <div>
            <SectionHead title="Your Claimed Vouchers" />
            {claimedVouchers.length === 0 ? (
              <p style={{ color: "#64748b", fontSize: "13px", padding: "20px 0", textAlign: "center" }}>
                You have not claimed any vouchers yet. Choose a partner offer above to redeem your EcoPoints!
              </p>
            ) : (
              <div style={{ display: "grid", gap: "10px" }}>
                {claimedVouchers.map((v) => (
                  <section className="reward-item" key={v.id}>
                    <span className="reward-icon r1">
                      <Icon name="gift" />
                    </span>
                    <div>
                      <Pill>{v.partner}</Pill>
                      <h2>{v.offerTitle}</h2>
                      <p>
                        Code: <b style={{ fontFamily: "monospace" }}>{v.code}</b> · {v.claimedAt}
                      </p>
                    </div>
                    <Button
                      kind="outline"
                      className="compact"
                      onClick={() => setActiveVoucher(v)}
                    >
                      View
                    </Button>
                  </section>
                ))}
              </div>
            )}
          </div>
        ) : (
          <div>
            <SectionHead title="Spend points at partner coffees & shops" />
            <div className="reward-list">
              {filteredOffers.map(([title, partner, cost, , icon]) => {
                const canAfford = balance >= cost;
                return (
                  <section className="reward-item" key={title}>
                    <span className={`reward-icon ${icon === "leaf" ? "r0" : "r1"}`}>
                      <Icon name={icon} />
                    </span>
                    <div>
                      <Pill>{partner}</Pill>
                      <h2>{title}</h2>
                      <p>{cost} EcoPoints</p>
                    </div>
                    <Button
                      kind="primary"
                      className="compact"
                      disabled={!canAfford}
                      onClick={() => handleRedeem(title, partner, cost)}
                    >
                      {canAfford ? "Redeem" : "Need points"}
                    </Button>
                  </section>
                );
              })}
            </div>
          </div>
        )}
      </div>
    </Page>
  );
}

function Notifications({ go, screen = "notifications", back = "home", role = "citizen" }: { go: (s: Screen) => void; screen?: Screen; back?: Screen; role?: "citizen" | "farmer" | "nursery" }) {
  const [read, setRead] = useState(false);
  const items = role === "farmer"
    ? [["drop","Moisture alert","North Field fell below the preferred moisture range.","8 min"],["globe","Satellite data ready","Fresh imagery is available for all three fields.","42 min"],["clock","Rain forecast changed","Expected rainfall moved to Wednesday evening.","2 hr"]]
    : role === "nursery"
      ? [["box","New order #1049","Amina K. placed a pickup order for three items.","5 min"],["alert","Stock needs attention","Peat-free Potting Mix is hidden until restocked.","36 min"],["chart","Store views are rising","Your public store received 84 views today.","3 hr"]]
      : [["gift","A reward is ready","Your 5-day streak unlocked a new local offer.","10 min"],["leaf","Mission reminder","Riverside Park starts Saturday at 10:00.","1 hr"],["people","Community update","18 guardians took action near you today.","3 hr"]];
  return <Page screen={screen} go={go}><DetailTop title="" back={back} go={go}/><div className="content top-content"><header className="notif-title"><span className="eyebrow">{role === "citizen" ? "Maya Chen" : role === "farmer" ? "Green Valley Farm" : "Green Garden Nursery"}</span><h1>{role === "citizen" ? "Your" : role === "farmer" ? "Farm" : "Store"} notifications</h1><p>{read ? "You’re all caught up." : "2 unread updates today"}</p></header><div className="section-head"><h2>Today</h2><Button kind="text" onClick={() => setRead(true)}>Mark all read</Button></div><div className="notification-list">{items.map(([icon,title,text,time],i)=><section className={read || i===2 ? "read" : ""} key={title}><i className="icon-tile"><Icon name={icon as IconName}/></i><div><b>{title}</b><p>{text}</p><small>{time}</small></div></section>)}</div><SectionHead title="Earlier"/><div className="notification-list"><section className="read"><i className="icon-tile"><Icon name="spark"/></i><div><b>{role === "farmer" ? "Weekly field summary ready" : role === "nursery" ? "Your Eco Partner badge is active" : "You moved up a level"}</b><p>{role === "farmer" ? "Crop health remained stable across 81% of monitored land." : role === "nursery" ? "Customers can see your verified sustainability status." : "You are now a Sapling. Keep growing."}</p><small>Yesterday</small></div></section></div></div></Page>;
}

function getParcelMetrics(
  parcel: ParcelItem,
  run?: AnalysisRunResponse | null,
  obs?: SceneObservationItem[],
  anoms?: ParcelAnomalyItem[],
) {
  const crop = parcel.label?.crop || parcel.season || "Wheat";
  const irrigation = parcel.label?.irrigation || "Monitored";
  const m1 = run?.result_summary?.m1;
  const m2 = run?.result_summary?.m2_anomaly || (anoms && anoms[0] ? {
    type: anoms[0].anomaly_type || "stable_canopy",
    severity: anoms[0].severity,
    delta_ndvi: anoms[0].delta_ndvi || 0,
    score: anoms[0].score,
  } : undefined);
  const m3 = run?.result_summary?.m3_recommendation;

  const latestObs = obs && obs[obs.length - 1];
  const ndvi = m1?.latest_ndvi_median ?? latestObs?.ndvi_median ?? (parcel.parcel_id === 1 || parcel.parcel_id === 194 ? 0.35 : 0.62);
  const coverPct = m1?.latest_cover_pct ?? latestObs?.vegetation_cover_pct ?? Math.round(ndvi * 100);
  const health = Math.min(98, Math.max(38, Math.round(ndvi * 100 + 32)));
  const moist = Math.min(65, Math.max(22, Math.round(ndvi * 48 + 14)));
  const alert = Boolean(m2 && m2.severity && m2.severity !== "none") || health < 62;

  return { crop, irrigation, ndvi, coverPct, health, moist, alert, m1, m2, m3 };
}

function FieldRows({
  filter = "All",
  farm,
  onSelect,
  selectedId,
  limit,
}: {
  filter?: string;
  farm: ReturnType<typeof useFarmStore>;
  onSelect?: (id: number) => void;
  selectedId?: number;
  limit?: number;
}) {
  const list = farm.parcels.slice(0, limit ?? farm.parcels.length).filter((p) => {
    const { alert } = getParcelMetrics(p, farm.analysisRuns[p.parcel_id], farm.observations[p.parcel_id], farm.anomalies[p.parcel_id]);
    return filter === "All" || (filter === "Needs attention" ? alert : !alert);
  });

  if (farm.loading && farm.parcels.length === 0) {
    return (
      <div className="list-card field-rows" style={{ textAlign: "center", padding: "24px" }}>
        <small>Connecting to Neon DB & loading 194 parcels...</small>
      </div>
    );
  }

  if (list.length === 0) {
    return (
      <div className="list-card field-rows" style={{ textAlign: "center", padding: "20px" }}>
        <small>No parcels match the current filter.</small>
      </div>
    );
  }

  return (
    <div className="list-card field-rows">
      {list.map((parcel) => {
        const { crop, irrigation, health, moist, alert, ndvi } = getParcelMetrics(
          parcel,
          farm.analysisRuns[parcel.parcel_id],
          farm.observations[parcel.parcel_id],
          farm.anomalies[parcel.parcel_id],
        );
        const isPicked = selectedId === parcel.parcel_id;
        return (
          <Button
            kind="text"
            key={parcel.parcel_id}
            className={isPicked ? "picked" : ""}
            onClick={() => onSelect?.(parcel.parcel_id)}
          >
            <Ring value={health} alert={alert} />
            <span>
              <b>Parcel #{parcel.parcel_id}</b>
              <small>{crop} · {irrigation}</small>
              <span className="field-tags">
                <em className={alert ? "attention" : ""}>{alert ? "Needs water" : "Healthy"}</em>
                <small>NDVI {ndvi.toFixed(2)} · Moist {moist}%</small>
              </span>
            </span>
            <Icon name="chevron" />
          </Button>
        );
      })}
    </div>
  );
}

function FarmerOverview({
  go,
  farm,
  user,
}: {
  go: (s: Screen) => void;
  farm: ReturnType<typeof useFarmStore>;
  user?: UserProfile;
}) {
  const [done, setDone] = useState<string[]>([]);
  const urgentParcel = farm.parcels.find((p) => {
    const m = getParcelMetrics(p, farm.analysisRuns[p.parcel_id], farm.observations[p.parcel_id], farm.anomalies[p.parcel_id]);
    return m.alert;
  }) || farm.parcels[0];

  const urgentMetrics = urgentParcel
    ? getParcelMetrics(urgentParcel, farm.analysisRuns[urgentParcel.parcel_id], farm.observations[urgentParcel.parcel_id], farm.anomalies[urgentParcel.parcel_id])
    : null;

  const tasks: [string, string][] = [
    [`Probe soil in Parcel #${urgentParcel?.parcel_id || 194} west edge`, "15 min · 3 spots"],
    ["Check drip irrigation pressure", "10 min · River sector"],
    ["Verify satellite NDVI anomaly scouting notes", "5 min · Al Haouz basin"],
  ];
  const week: [string, IconName, string, string][] = [
    ["Today", "spark", "24°", "0 mm"],
    ["Thu", "spark", "26°", "0 mm"],
    ["Fri", "spark", "28°", "0 mm"],
    ["Sat", "drop", "21°", "6 mm"],
    ["Sun", "drop", "19°", "11 mm"],
  ];

  const farmerFirst = user?.name ? user.name.split(" ")[0] : "Samir";
  const farmTitle = user?.farmName || "Green Valley Farm · Al Haouz";

  return (
    <Page screen="farmer-overview" role="farmer" go={go}>
      <div className="content">
        <Header
          eyebrow={farmTitle}
          title={`Good morning, ${farmerFirst}`}
          caption="A calm overview of what is thriving and what needs care."
          onBell={() => go("farmer-notifications")}
        />
        <ContextStrip
          icon="shield"
          title="Neon DB & eco-worker connected"
          text={`${farm.totalParcels} parcels active · Sentinel-2 satellite pipeline online`}
        />
        <div className="photo-banner">
          <img src={PHOTO_FIELD} alt="Aerial view of Green Valley Farm fields" />
          <i />
          <span>{farm.totalParcels} golden parcels · 26.3 ha monitored</span>
        </div>

        {/* Live Interactive Sentinel-2 Basin Map Preview */}
        <section className="farm-map" style={{ padding: 0, overflow: "hidden", marginTop: "14px", borderRadius: "18px" }}>
          <header className="map-head" style={{ padding: "12px 14px 6px" }}>
            <div>
              <h2>Al Haouz Basin Map</h2>
              <small>Live Sentinel-2 vegetation layer</small>
            </div>
            <Button kind="outline" className="compact" onClick={() => go("fields")}>
              Full Map ({farm.totalParcels}) →
            </Button>
          </header>
          <ParcelMap
            parcels={farm.parcels}
            selectedParcelId={urgentParcel?.parcel_id ?? 194}
            onSelectParcel={(id) => {
              farm.selectParcel(id);
              go("fields");
            }}
            layer="ndvi"
            analysisRuns={farm.analysisRuns}
            observations={farm.observations}
            anomalies={farm.anomalies}
            height={260}
          />
        </section>
        {urgentParcel && urgentMetrics && (
          <section className="attention leaf-cut">
            <i className="icon-tile peach"><Icon name="drop" /></i>
            <span className="eyebrow">Needs a look today</span>
            <h2>Parcel #{urgentParcel.parcel_id} ({urgentMetrics.crop}) needs a check</h2>
            <p>
              {urgentMetrics.m2?.type === "stable_canopy"
                ? `Canopy is stable (NDVI ${urgentMetrics.ndvi.toFixed(2)}). Continue scheduled monitoring.`
                : `Moisture readings are trending low (${urgentMetrics.moist}%). Confirm conditions before irrigating.`}
            </p>
            <Button
              onClick={() => {
                farm.selectParcel(urgentParcel.parcel_id);
                go("advice");
              }}
            >
              See what to do
            </Button>
          </section>
        )}
        <Stats
          items={[
            ["24°C", "Temperature"],
            [`${urgentMetrics ? urgentMetrics.moist : 41}%`, "Soil moisture"],
            [`${farm.totalParcels}`, "Monitored parcels"],
          ]}
        />
        <section className="card soil">
          <div>
            <h2>Soil moisture, last 7 days</h2>
            <Pill tone="peach">Falling</Pill>
          </div>
          <svg viewBox="0 0 320 60" aria-label="Falling soil moisture chart">
            <path d="M3 12 55 17l52 2 52 13 52 3 52 12 52 8" />
            <circle cx="315" cy="55" r="4" />
          </svg>
          <p>Down 9% since last Monday across Al Haouz basin</p>
        </section>
        <SectionHead title="5-day forecast" />
        <div className="weather-strip">
          {week.map(([d, ic, t, r], i) => (
            <div key={d} className={i === 0 ? "today" : ""}>
              <small>{d}</small>
              <Icon name={ic} size={20} />
              <b>{t}</b>
              <span>{r}</span>
            </div>
          ))}
        </div>
        <SectionHead title={`Today's tasks · ${done.length}/${tasks.length}`} />
        <div className="task-list">
          {tasks.map(([t, m]) => {
            const on = done.includes(t);
            return (
              <button
                key={t}
                className={on ? "on" : ""}
                onClick={() => setDone(on ? done.filter((x) => x !== t) : [...done, t])}
              >
                <i>{on && <Icon name="check" size={14} />}</i>
                <span>
                  <b>{t}</b>
                  <small>{m}</small>
                </span>
              </button>
            );
          })}
        </div>
        <SectionHead title="Your fields" action="View all" />
        <FieldRows
          farm={farm}
          limit={5}
          onSelect={(id) => {
            farm.selectParcel(id);
            go("fields");
          }}
        />
      </div>
    </Page>
  );
}

function Fields({ go, farm, user }: { go: (s: Screen) => void; farm: ReturnType<typeof useFarmStore>; user?: UserProfile }) {
  const [layer, setLayer] = useState<"ndvi" | "moist" | "anomaly">("ndvi");
  const [jumpInput, setJumpInput] = useState("");

  const currentParcel = farm.selectedParcel || farm.parcels[0];
  const parcelId = currentParcel?.parcel_id ?? 194;
  const currentRun = farm.analysisRuns[parcelId];
  const metrics = currentParcel
    ? getParcelMetrics(
        currentParcel,
        currentRun,
        farm.observations[parcelId],
        farm.anomalies[parcelId],
      )
    : { crop: "Wheat", irrigation: "Flood", ndvi: 0.35, coverPct: 35, health: 65, moist: 30, alert: true, m1: undefined, m2: undefined, m3: undefined };

  const isAnalyzing = farm.analyzingParcelId === parcelId;

  const handleJump = (e: React.FormEvent) => {
    e.preventDefault();
    const id = parseInt(jumpInput.trim(), 10);
    if (!isNaN(id) && id >= 1 && id <= 194) {
      farm.selectParcel(id);
      setJumpInput("");
    }
  };

  const quickParcels = [194, 14, 5, 42, 88, 112, 150];

  return (
    <Page screen="fields" role="farmer" go={go}>
      <TopBar title={`Map & Parcel #${parcelId}`} action={<Icon name="field" />} user={user} onLogout={() => go("signup")} />
      <div className="content top-content">
        <section className="farm-summary">
          <div>
            <span className="eyebrow">Al Haouz Agricultural Basin · Morocco</span>
            <h1>Parcel #{parcelId}<small> · {metrics.crop} ({metrics.irrigation})</small></h1>
          </div>
          <div className="sync">
            <i />Sentinel-2 · live satellite
          </div>
          <div className="summary-row">
            <span><b>{metrics.health}%</b><small>Health</small></span>
            <span><b>{metrics.ndvi.toFixed(2)}</b><small>NDVI</small></span>
            <span className={metrics.alert ? "warn" : ""}>
              <b>{metrics.moist}%</b><small>{metrics.alert ? "Water stress" : "Moisture"}</small>
            </span>
          </div>
        </section>

        {/* Real Interactive Leaflet Parcel Map */}
        <section className="farm-map" style={{ padding: 0, overflow: "hidden", borderRadius: "20px" }}>
          <header className="map-head" style={{ padding: "12px 14px 6px" }}>
            <div>
              <h2>Interactive Sentinel-2 Satellite Map</h2>
              <small>Tap any of the 194 parcels on the map to load its specific telemetry</small>
            </div>
            <span><Icon name="field" size={13} /> {farm.parcels.length} parcels</span>
          </header>
          <ParcelMap
            parcels={farm.parcels}
            selectedParcelId={parcelId}
            onSelectParcel={(id) => farm.selectParcel(id)}
            layer={layer}
            onLayerChange={(l) => setLayer(l)}
            analysisRuns={farm.analysisRuns}
            observations={farm.observations}
            anomalies={farm.anomalies}
            height={410}
          />
        </section>

        {/* Quick Parcel Selector & Jump Bar */}
        <section style={{ margin: "10px 0 6px", display: "grid", gap: "8px" }}>
          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
            <span style={{ fontSize: "12px", fontWeight: 700, color: "var(--forest-800)" }}>
              📍 Jump to Parcel:
            </span>
            <form onSubmit={handleJump} style={{ display: "flex", gap: "6px" }}>
              <input
                type="number"
                min="1"
                max="194"
                value={jumpInput}
                onChange={(e) => setJumpInput(e.target.value)}
                placeholder="Parcel # (1-194)"
                style={{
                  width: "120px",
                  padding: "6px 10px",
                  borderRadius: "10px",
                  border: "1px solid var(--sage-100)",
                  fontSize: "12px",
                  background: "#fff",
                }}
              />
              <Button type="submit" kind="outline" className="compact">Go</Button>
            </form>
          </div>

          <div className="parcel-quick-bar">
            {quickParcels.map((id) => (
              <button
                key={id}
                type="button"
                className={`parcel-quick-btn ${parcelId === id ? "active" : ""}`}
                onClick={() => farm.selectParcel(id)}
              >
                Parcel #{id} {id === 194 ? "⚠️" : ""}
              </button>
            ))}
          </div>

          <div
            style={{
              padding: "10px 14px",
              borderRadius: "14px",
              background: "var(--cream-100, #F4F8F4)",
              border: "1px solid var(--sage-100, #C2D8C6)",
              fontSize: "12px",
              color: "var(--forest-800)",
              display: "flex",
              alignItems: "center",
              gap: "8px",
            }}
          >
            <Icon name="pin" size={16} />
            <span>
              Tap any parcel boundary directly on the satellite map above to switch and inspect its vegetation health.
            </span>
          </div>
        </section>

        {/* Selected Parcel Deep-Dive Telemetry Panel */}
        {currentParcel && (
          <section className={`field-panel ${metrics.alert ? "alert" : ""}`}>
            <header>
              <div>
                <span className="eyebrow">{metrics.crop} · Parcel #{currentParcel.parcel_id}</span>
                <h2>Parcel #{currentParcel.parcel_id} Deep Analysis</h2>
                <small>{metrics.irrigation} · {currentParcel.zone || "Al Haouz Basin"} · Observed {currentParcel.observation_date}</small>
              </div>
              <Pill tone={metrics.alert ? "peach" : "sage"}>
                {metrics.alert ? "Needs Water" : "Healthy Vigor"}
              </Pill>
            </header>

            <div className="field-metrics">
              <div>
                <b>{metrics.health}%</b>
                <small>Health Score</small>
              </div>
              <div>
                <b className={metrics.moist < 35 ? "low" : ""}>{metrics.moist}%</b>
                <small>Est. NDMI Moisture</small>
              </div>
              <div>
                <b>{metrics.ndvi.toFixed(2)}</b>
                <small>Latest S2 NDVI</small>
              </div>
            </div>

            {/* Satellite Analysis Worker Trigger */}
            <div style={{ marginTop: "6px" }}>
              {isAnalyzing ? (
                <div style={{ padding: "14px", borderRadius: "16px", background: "var(--cream-100)", display: "grid", gap: "8px" }}>
                  <div style={{ display: "flex", alignItems: "center", gap: "8px", fontWeight: 700, fontSize: "13px" }}>
                    <Icon name="spark" size={16} />
                    <span>Analyzing Parcel #{parcelId} via Railway Worker...</span>
                  </div>
                  <small style={{ color: "var(--forest-700)" }}>{farm.analyzingStatusText}</small>
                  <Progress value={80} />
                </div>
              ) : (
                <Button
                  className="full"
                  kind={currentRun ? "outline" : "primary"}
                  onClick={() => farm.runAnalysis(parcelId)}
                >
                  <Icon name="spark" size={17} />
                  {currentRun ? "Re-run Sentinel-2 Telemetry" : "Run Sentinel-2 Satellite Analysis"}
                </Button>
              )}
            </div>

            {/* Completed Analysis Summary Card */}
            {currentRun && currentRun.result_summary && (
              <div style={{ padding: "12px 14px", borderRadius: "16px", background: "#F1F6F1", border: "1px solid var(--sage-100)", display: "grid", gap: "6px" }}>
                <span className="eyebrow" style={{ fontSize: "11px" }}>🛰️ Sentinel-2 AI Agronomic Findings</span>
                <div style={{ fontSize: "13px", lineHeight: "19px", color: "var(--forest-900)" }}>
                  <b>Vegetation Vigor:</b> {metrics.coverPct}% cover · Median NDVI: {metrics.ndvi.toFixed(2)}<br />
                  <b>Moisture & Anomaly:</b> {metrics.m2?.type || "stable"} ({metrics.m2?.severity || "none"} severity, ΔNDVI: {metrics.m2?.delta_ndvi ?? 0})<br />
                  <b>Agronomic Guidance:</b> {metrics.m3?.title || "Normal monitoring schedule"}
                </div>
              </div>
            )}

            <div className="field-trend">
              <div>
                <small>Multi-temporal NDVI Trend</small>
                <b className={metrics.alert ? "down" : "up"}>
                  {metrics.alert ? "Declining -0.04" : "Stable +0.02"}
                </b>
              </div>
              <svg viewBox="0 0 120 36" preserveAspectRatio="none">
                <polyline points="0,36 20,24 40,22 60,18 80,19 100,23 120,25 120,36" className="area" />
                <polyline points="0,26 20,24 40,22 60,18 80,19 100,23 120,25" className={`ln ${metrics.alert ? "warn" : ""}`} />
              </svg>
            </div>

            <ul className="field-facts">
              <li>
                <Icon name="shield" size={15} />
                Sentinel-2 L2A multi-spectral rasters via Cloudflare R2
              </li>
              <li>
                <Icon name="clock" size={15} />
                {currentParcel.observation_date} · Cropping Season: {currentParcel.season}
              </li>
              <li>
                <Icon name="leaf" size={15} />
                Soil: Clay-loam alluvial · Zone: {currentParcel.zone || "Al Haouz"}
              </li>
            </ul>

            <div className="field-actions">
              <Button
                className="compact"
                onClick={() => go(metrics.alert ? "advice" : "risks")}
              >
                {metrics.alert ? "Recommended Advice" : "Risk Outlook"}
              </Button>
              <Button
                className="compact"
                kind="outline"
                onClick={() => go("assistant")}
              >
                Ask Assistant about P#{parcelId}
              </Button>
            </div>
          </section>
        )}
      </div>
    </Page>
  );
}

function Advice({ go, farm }: { go: (s: Screen) => void; farm: ReturnType<typeof useFarmStore> }) {
  const [doneSteps, setDoneSteps] = useState<string[]>([]);
  const currentParcel = farm.selectedParcel || farm.parcels[0];
  const parcelId = currentParcel?.parcel_id ?? 194;
  const currentRun = farm.analysisRuns[parcelId];
  const metrics = currentParcel ? getParcelMetrics(currentParcel, currentRun) : null;
  const m3 = currentRun?.result_summary?.m3_recommendation;

  const checks = m3?.field_checks && m3.field_checks.length > 0
    ? m3.field_checks
    : [
        "Inspect lower canopy leaves for early curling or drought distress.",
        "Verify drip and surface line pressure across western parcel quadrant.",
        "Check soil moisture at 8–10 cm depth in three representative zones before pumping.",
      ];

  const citations = m3?.citations && m3.citations.length > 0
    ? m3.citations
    : [
        {
          chunk_id: "ONCA-MOR-GENERAL-05",
          title: "Standard Field Scouting Protocol for Satellite Anomaly Ground-Truthing",
          organization: "ONCA Morocco",
          source_url: "https://www.onca.gov.ma/guidelines/ground-scouting-remote-sensing",
        },
        {
          chunk_id: "FAO-ICARDA-BARLEY-03",
          title: "Drought Adaptation and Salinity Management in Lowland Barley",
          organization: "ICARDA",
          source_url: "https://www.icarda.org/research/barley-arid-zones-morocco",
        },
      ];

  return (
    <Page screen="advice" role="farmer" go={go}>
      <TopBar title={`Advice · P#${parcelId}`} />
      <div className="content top-content">
        <ContextStrip
          icon="spark"
          title={`${doneSteps.length} of ${checks.length} field checks done`}
          text="Prioritized by INRA agronomic guidance & crop impact"
        />
        <p className="intro">Targeted agronomic steps grounded in Sentinel-2 telemetry.</p>

        <SectionHead title="Priority action" />
        <section className="hero advice-hero">
          <Topo />
          <Pill tone="peach">Parcel #{parcelId} · {metrics?.crop || "Wheat"}</Pill>
          <h1>{m3?.title || "Verify soil moisture before irrigating"}</h1>
          <p>{m3?.summary || "Ground-truth satellite indicators by checking root-zone moisture before scheduling supplemental irrigation."}</p>
          <div style={{ display: "flex", gap: "8px", marginTop: "14px" }}>
            <Button kind="dark" onClick={() => go("fields")}>
              Inspect parcel in map
            </Button>
            {!m3 && (
              <Button
                kind="outline"
                onClick={() => farm.runAnalysis(parcelId)}
              >
                Run S2 Analysis
              </Button>
            )}
          </div>
        </section>

        <SectionHead title="Field inspection checks" />
        <div className="advice-list">
          {checks.map((checkText, idx) => {
            const stepNum = String(idx + 1).padStart(2, "0");
            const on = doneSteps.includes(stepNum);
            return (
              <section key={stepNum} className={on ? "done" : ""}>
                <b>{stepNum}</b>
                <div>
                  <h2>{checkText}</h2>
                  <small>Parcel #{parcelId} · Protocol Step · 15 min</small>
                </div>
                <button
                  aria-label="Mark done"
                  onClick={() =>
                    setDoneSteps(on ? doneSteps.filter((x) => x !== stepNum) : [...doneSteps, stepNum])
                  }
                >
                  {on && <Icon name="check" size={14} />}
                </button>
              </section>
            );
          })}
        </div>

        <SectionHead title="Vetted agronomic citations" />
        <div style={{ display: "grid", gap: "10px" }}>
          {citations.map((c) => (
            <div
              key={c.chunk_id}
              className="card"
              style={{ padding: "14px 16px", display: "grid", gap: "6px" }}
            >
              <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                <Pill tone="sage">{c.organization}</Pill>
                <small style={{ color: "var(--muted)", fontWeight: 700 }}>{c.chunk_id}</small>
              </div>
              <b style={{ fontSize: "14px", lineHeight: "19px", color: "var(--forest-900)" }}>{c.title}</b>
              <a
                href={c.source_url}
                target="_blank"
                rel="noreferrer"
                style={{ fontSize: "12px", color: "var(--green-500)", fontWeight: 700, textDecoration: "underline" }}
              >
                View publication →
              </a>
            </div>
          ))}
        </div>

        <section className="attention expert" style={{ marginTop: "16px" }}>
          <i className="icon-tile"><Icon name="chat" /></i>
          <div>
            <h2>Have questions about this parcel?</h2>
            <p>Consult with the Ninhursag agronomist AI assistant.</p>
          </div>
          <Button onClick={() => go("assistant")}>Ask</Button>
        </section>
      </div>
    </Page>
  );
}

function Risks({ go, farm }: { go: (s: Screen) => void; farm: ReturnType<typeof useFarmStore> }) {
  const [day, setDay] = useState(4);
  const currentParcel = farm.selectedParcel || farm.parcels[0];
  const parcelId = currentParcel?.parcel_id ?? 194;
  const currentRun = farm.analysisRuns[parcelId];
  const metrics = currentParcel ? getParcelMetrics(currentParcel, currentRun) : null;
  const m2 = metrics?.m2;

  const dayInfo = [
    ["Mon", 12, "Low", "Soil holds sufficient moisture.", "22°", "0", "46%"],
    ["Tue", 20, "Low", "Mild heat, vegetative vigor normal.", "23°", "0", "43%"],
    ["Wed", 29, "Moderate", "Moisture approaches stress threshold. Scout western gradient.", "25°", "0", "39%"],
    ["Thu", 40, "Moderate", "Prepare regulated deficit irrigation for early morning.", "26°", "0", "35%"],
    ["Fri", 58, "High", "28°C and dry wind. Irrigate before 07:00 if verified dry.", "28°", "0", "31%"],
    ["Sat", 52, "High", "Light rain eases stress slightly. Skip midday watering.", "21°", "6", "33%"],
    ["Sun", 45, "Moderate", "Soil recovery expected.", "19°", "11", "38%"],
  ] as const;

  const isDroughtElevated = (m2 && m2.delta_ndvi < -0.05) || (metrics && metrics.moist < 35);
  const isDeclineElevated = m2?.type === "vegetation_decline";

  const risks = [
    [
      "alert" as IconName,
      isDroughtElevated ? "Elevated" : "Low",
      "Drought & moisture stress",
      isDroughtElevated
        ? `Parcel #${parcelId} shows water stress (NDVI ${metrics?.ndvi.toFixed(2)}). Irrigation may be needed by Friday.`
        : "Soil moisture within safe operating band across all monitored sectors.",
      isDroughtElevated,
    ],
    [
      "shield" as IconName,
      isDeclineElevated ? "Moderate" : "Low",
      "Canopy vigor change",
      isDeclineElevated
        ? "Multi-temporal satellite comparison indicates canopy decline. Inspect for leaf roll."
        : `Stable vegetative canopy (ΔNDVI: ${m2?.delta_ndvi ?? 0.0}). No abrupt vigor decline.`,
      isDeclineElevated,
    ],
    [
      "shield" as IconName,
      "Low",
      "Heat stress & wildfire",
      "Temperatures within typical range for Al Haouz winter crops.",
      false,
    ],
  ] as const;

  return (
    <Page screen="risks" role="farmer" go={go}>
      <TopBar title={`Risk outlook · P#${parcelId}`} />
      <div className="content top-content">
        <ContextStrip
          icon="clock"
          title="Satellite telemetry synced"
          text={`Parcel #${parcelId} (${metrics?.crop || "Wheat"}) · Baseline vs current scene evaluated`}
        />
        <section className="hero risk-hero">
          <Topo />
          <span className="eyebrow">Next 7 days</span>
          <h1>Drought stress evaluation for Parcel #{parcelId}</h1>
          <p>
            {m2?.type === "stable_canopy"
              ? "Sentinel-2 scenes show stable canopy vigor across January acquisitions."
              : "Satellite telemetry flags moisture stress along the western parcel gradient."}
          </p>
        </section>

        <div className="risk-list">
          {risks.map(([icon, level, title, text, elevated]) => (
            <section
              className={`risk-card ${elevated ? "elevated" : ""}`}
              key={title}
            >
              <i className={`icon-tile ${elevated ? "peach" : ""}`}>
                <Icon name={icon} />
              </i>
              <span className="eyebrow">{level}</span>
              <h2>{title}</h2>
              <p>{text}</p>
              {elevated && (
                <>
                  <Progress value={74} />
                  <small>74% confidence · Sentinel-2 calibrated</small>
                </>
              )}
            </section>
          ))}
        </div>

        {/* Multi-temporal Sentinel-2 Scenes strip */}
        <section className="card" style={{ padding: "16px", marginTop: "14px", display: "grid", gap: "8px" }}>
          <span className="eyebrow">Multi-Temporal Sentinel-2 Scenes</span>
          <div style={{ display: "grid", gridTemplateColumns: "repeat(3, 1fr)", gap: "8px", textAlign: "center" }}>
            <div style={{ padding: "8px", background: "var(--cream-100)", borderRadius: "12px" }}>
              <small style={{ color: "var(--muted)" }}>2024-01-11</small>
              <b style={{ display: "block", fontSize: "14px", color: "var(--forest-900)" }}>Baseline</b>
              <span style={{ fontSize: "11px", color: "var(--green-500)", fontWeight: 700 }}>NDVI 0.35</span>
            </div>
            <div style={{ padding: "8px", background: "var(--cream-100)", borderRadius: "12px" }}>
              <small style={{ color: "var(--muted)" }}>2024-01-21</small>
              <b style={{ display: "block", fontSize: "14px", color: "var(--forest-900)" }}>Mid-season</b>
              <span style={{ fontSize: "11px", color: "var(--green-500)", fontWeight: 700 }}>NDVI 0.35</span>
            </div>
            <div style={{ padding: "8px", background: "#F1F6F1", border: "1.5px solid var(--green-500)", borderRadius: "12px" }}>
              <small style={{ color: "var(--forest-700)", fontWeight: 700 }}>2024-01-26</small>
              <b style={{ display: "block", fontSize: "14px", color: "var(--forest-900)" }}>Current</b>
              <span style={{ fontSize: "11px", color: "var(--green-500)", fontWeight: 700 }}>NDVI {metrics?.ndvi.toFixed(2)}</span>
            </div>
          </div>
        </section>

        <section className="card moist-chart">
          <header>
            <div>
              <span className="eyebrow">Parcel #{parcelId} · next 7 days</span>
              <h2>When the risk peaks</h2>
            </div>
            <span className="peak-chip">
              <Icon name="drop" size={14} />
              Driest Fri · 31%
            </span>
          </header>
          <svg viewBox="0 0 320 150" className="moist-svg" aria-label="Soil moisture forecast">
            {(() => {
              const pts = dayInfo.map(([, , , , , , m], i) => [
                20 + i * 46.7,
                130 - (parseInt(m) - 25) * 4.5,
              ]);
              const line = pts.map((p, i) => `${i ? "L" : "M"}${p[0]} ${p[1]}`).join(" ");
              const dryY = 130 - (35 - 25) * 4.5;
              return (
                <>
                  <defs>
                    <linearGradient id="mfill" x1="0" x2="0" y1="0" y2="1">
                      <stop offset="0" stopColor="#4E7A5A" stopOpacity=".28" />
                      <stop offset="1" stopColor="#4E7A5A" stopOpacity="0" />
                    </linearGradient>
                  </defs>
                  <rect
                    x="0"
                    y={dryY}
                    width="320"
                    height={150 - dryY}
                    rx="10"
                    className="dry-zone"
                  />
                  <text x="8" y={dryY + 14} className="zone-label">
                    Dry zone · below 35%
                  </text>
                  <path
                    d={`${line} L${pts[6][0]} 150 L${pts[0][0]} 150Z`}
                    fill="url(#mfill)"
                  />
                  <path d={line} className="moist-line" />
                  {pts.map((p, i) => (
                    <g
                      key={i}
                      onClick={() => setDay(i)}
                      className={`pt ${day === i ? "sel" : ""} ${parseInt(dayInfo[i][6]) < 35 ? "dry" : ""}`}
                    >
                      {day === i && <line x1={p[0]} x2={p[0]} y1="0" y2="150" />}
                      <circle cx={p[0]} cy={p[1]} r={day === i ? 7 : 5} />
                      <circle cx={p[0]} cy={p[1]} r="18" className="hit" />
                      {day === i && (
                        <text x={p[0]} y={p[1] - 14} textAnchor="middle">
                          {dayInfo[i][6]}
                        </text>
                      )}
                    </g>
                  ))}
                </>
              );
            })()}
          </svg>
          <div className="moist-days">
            {dayInfo.map(([d, , , , t, r], i) => (
              <button
                key={d}
                className={day === i ? "sel" : ""}
                onClick={() => setDay(i)}
              >
                <b>{d}</b>
                <small>{t}</small>
                <span className={r !== "0" ? "rain" : ""}>{r === "0" ? "Dry" : `${r}mm`}</span>
              </button>
            ))}
          </div>
          <div className="risk-day">
            <header>
              <b>
                {
                  [
                    "Monday",
                    "Tuesday",
                    "Wednesday",
                    "Thursday",
                    "Friday",
                    "Saturday",
                    "Sunday",
                  ][day]
                }
              </b>
              <Pill tone={dayInfo[day][1] >= 50 ? "peach" : "sage"}>
                {dayInfo[day][2]} · {dayInfo[day][1]}/100
              </Pill>
            </header>
            <div className="risk-metrics">
              <div>
                <b>{dayInfo[day][4]}</b>
                <small>Max temp</small>
              </div>
              <div>
                <b>{dayInfo[day][5]} mm</b>
                <small>Rain</small>
              </div>
              <div>
                <b>{dayInfo[day][6]}</b>
                <small>Soil moisture</small>
              </div>
            </div>
            <p><Icon name="spark" size={15} />{dayInfo[day][3]}</p>
          </div>
        </section>
      </div>
    </Page>
  );
}

function Assistant({ go, farm, user }: { go: (s: Screen) => void; farm: ReturnType<typeof useFarmStore>; user?: UserProfile }) {
  const currentParcel = farm.selectedParcel || farm.parcels[0];
  const parcelId = currentParcel?.parcel_id ?? 194;
  const currentRun = farm.analysisRuns[parcelId];
  const metrics = currentParcel ? getParcelMetrics(currentParcel, currentRun) : null;
  const crop = metrics?.crop || "Wheat";
  const irrigation = metrics?.irrigation || "Monitored";
  const ndvi = metrics?.ndvi ?? 0.35;

  const [input, setInput] = useState("");
  const farmerFirst = user?.name ? user.name.split(" ")[0] : "Farmer";
  const [messages, setMessages] = useState<Array<{ sender: "bot" | "user"; text: string }>>([
    {
      sender: "bot",
      text: `Hello ${farmerFirst}! I am your Ninhursag Agronomic Assistant, connected to Parcel #${parcelId} (${crop}, ${irrigation} in ${currentParcel?.zone || "Al Haouz"}). Current Sentinel-2 NDVI is ${ndvi.toFixed(2)}. How can I help you today?`,
    },
  ]);

  const handleSend = (userText?: string) => {
    const textToSend = (userText || input).trim();
    if (!textToSend) return;

    const newMsgs = [...messages, { sender: "user" as const, text: textToSend }];
    setMessages(newMsgs);
    if (!userText) setInput("");

    // Generate grounded agronomic response
    setTimeout(() => {
      let botReply = "";
      const lower = textToSend.toLowerCase();

      if (lower.includes("ndvi") || lower.includes("vigor")) {
        botReply = `Parcel #${parcelId} (${crop}) has an NDVI median of ${ndvi.toFixed(2)}. For winter crops in Al Haouz during January, values between 0.30 and 0.45 represent initial vegetative tillering. Canopy cover is estimated at ${metrics?.coverPct}%. Conditions are currently stable with no acute vegetative crash.`;
      } else if (lower.includes("check") || lower.includes("scout")) {
        botReply = `According to the ONCA Morocco ground-scouting protocol (ONCA-MOR-GENERAL-05), conduct these 3 checks on Parcel #${parcelId}:\n1. Scout 3 points along the western parcel boundary.\n2. Inspect root-zone soil at 8–10 cm depth.\n3. Check lower leaf undersides for early curling or drought distress.`;
      } else if (lower.includes("irrigat") || lower.includes("water")) {
        botReply = `For ${crop} under ${irrigation}, INRA Morocco recommends applying deficit irrigation early in the morning (before 07:00) to cut evaporative losses by up to 25%. Always verify soil moisture with a manual soil probe before turning on pumps to preserve groundwater.`;
      } else if (lower.includes("anomal") || lower.includes("satellite") || lower.includes("risk")) {
        botReply = `Multi-temporal analysis of Sentinel-2 scenes (2024-01-11 baseline to 2024-01-26 current) indicates a status of '${metrics?.m2?.type || "stable_canopy"}' with severity '${metrics?.m2?.severity || "none"}'. Delta NDVI is ${metrics?.m2?.delta_ndvi ?? 0.0}. Normal monitoring is recommended.`;
      } else {
        botReply = `Regarding Parcel #${parcelId} (${crop}, ${irrigation}): I have analyzed the latest telemetry and INRA Morocco publications. The crop is in vegetative growth in ${currentParcel?.zone || "Al Haouz"}. Prioritize early morning irrigation, verify soil moisture at 10cm, and record observations after any treatment.`;
      }

      setMessages((prev) => [...prev, { sender: "bot", text: botReply }]);
    }, 450);
  };

  return (
    <Page screen="assistant" go={go} className="assistant-page">
      <DetailTop title="Field assistant" back="advice" go={go} />
      <div className="assistant-status">
        <span className="big-avatar"><Icon name="leaf" /></span>
        <div>
          <h2>Ninhursag Agronomic Assistant</h2>
          <span><i />Grounded in Parcel #{parcelId} · {crop} ({irrigation})</span>
        </div>
      </div>

      <div className="chat-thread">
        <span className="day-label">Telemetry Live · Neon DB + Sentinel-2</span>
        {messages.map((m, idx) => (
          <div key={idx} className={`message ${m.sender}`}>
            {m.text}
          </div>
        ))}
      </div>

      {/* Suggested quick prompt chips */}
      <div style={{ padding: "0 20px 80px", display: "flex", flexWrap: "wrap", gap: "6px" }}>
        {[
          `Explain NDVI for P#${parcelId}`,
          "What field checks should I do?",
          `Irrigation advice for ${crop}`,
          "Explain satellite anomaly",
        ].map((prompt) => (
          <button
            key={prompt}
            onClick={() => handleSend(prompt)}
            style={{
              padding: "6px 12px",
              borderRadius: "99px",
              border: "1px solid var(--sage-200)",
              background: "var(--white)",
              fontSize: "12px",
              fontWeight: 600,
              color: "var(--forest-700)",
              cursor: "pointer",
            }}
          >
            {prompt}
          </button>
        ))}
      </div>

      <div className="chat-compose">
        <input
          value={input}
          onChange={(e) => setInput(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === "Enter") handleSend();
          }}
          placeholder={`Ask about Parcel #${parcelId} (${crop})...`}
        />
        <Button kind="icon" label="Send message" onClick={() => handleSend()}>
          <Icon name="chat" />
        </Button>
      </div>
    </Page>
  );
}

function MenuList({ items }: { items: [IconName, string][] }) {
  return (
    <section className="list-card menu">
      {items.map(([icon, label]) => (
        <Button kind="text" key={label}>
          <i className="icon-tile"><Icon name={icon} /></i>
          <b>{label}</b>
          <Icon name="chevron" />
        </Button>
      ))}
    </section>
  );
}

function FarmProfile({
  go,
  farm,
  user,
  onUpdateAvatar,
}: {
  go: (s: Screen) => void;
  farm: ReturnType<typeof useFarmStore>;
  user?: UserProfile;
  onUpdateAvatar?: (url: string) => void;
}) {
  const season: [string, string][] = [
    ["Sow", "Oct"],
    ["Tiller", "Jan"],
    ["Stem", "Mar"],
    ["Head", "May"],
    ["Harvest", "Jul"],
  ];
  const devices: [string, string, number, boolean][] = [
    ["Neon PostGIS DB", `${farm.totalParcels} golden parcels seeded`, 100, true],
    ["Cloudflare R2", "18 multi-temporal GeoTIFF rasters", 98, true],
    ["Railway eco-worker", "Sentinel-2 daemon active", 95, true],
    ["INRA Vector DB", "384-d semantic agronomy index", 100, true],
  ];

  const name = user?.name || "Samir Khalil";
  const initials = name
    .split(" ")
    .map((w) => w[0])
    .slice(0, 2)
    .join("")
    .toUpperCase();
  const sub = `${user?.farmName || "Green Valley Farm"} · Al Haouz, Morocco`;

  return (
    <Page screen="farm-profile" role="farmer" go={go}>
      <TopBar title="Farm profile" action={<Icon name="gear" />} user={user} onLogout={() => go("signup")} />
      <div className="content top-content profile">
        <ProfileCover
          photo={PHOTO_FIELD}
          initials={initials}
          name={name}
          sub={sub}
          tag="Verified farm"
          avatarPhoto={user?.avatar}
          onUploadAvatar={onUpdateAvatar}
        />
        <Stats
          items={[
            [`${farm.totalParcels}`, "Fields"],
            ["26.3", "Hectares"],
            ["4", "Live services"],
          ]}
        />
        <section className="card season-card">
          <div className="season-head">
            <div>
              <span className="eyebrow">2024 season · winter crops</span>
              <h2>Tillering & vegetative stage</h2>
            </div>
            <b>40%</b>
          </div>
          <div className="season-track">
            <i style={{ width: "40%" }} />
            {season.map(([n, m], i) => (
              <span key={n} className={i <= 1 ? "on" : ""} style={{ left: `${i * 25}%` }}>
                <em />
                <b>{n}</b>
                <small>{m}</small>
              </span>
            ))}
          </div>
          <p>Multi-temporal Sentinel-2 monitoring active across Al Haouz</p>
        </section>
        <SectionHead title="Connected data systems" />
        <section className="list-card device-list">
          {devices.map(([n, w, bat, ok]) => (
            <div key={n}>
              <i className={`icon-tile ${ok ? "" : "peach"}`}>
                <Icon name={ok ? "shield" : "alert"} size={18} />
              </i>
              <span>
                <b>{n}</b>
                <small>{w}</small>
              </span>
              <span className={`battery ${bat < 20 ? "low" : ""}`}>
                <i style={{ width: `${bat}%` }} />
              </span>
            </div>
          ))}
        </section>
        <section className="cert-row">
          <span><Icon name="leaf" size={16} />INRA Morocco Grounded</span>
          <span><Icon name="drop" size={16} />Water-Smart Remote Sensing</span>
        </section>
        <SectionHead title="Alerts" />
        <PrefToggles
          items={[
            ["Satellite anomaly alerts", "When NDVI drops by >0.10", true],
            ["Moisture stress warnings", "When NDMI drops below threshold", true],
            ["Weekly agronomic summary", "Email summary of all parcels", false],
          ]}
        />
        <MenuList
          items={[
            ["field", "Farm and parcel details"],
            ["chat", "Agronomy expert support"],
            ["lock", "Data and security"],
          ]}
        />
        <LogoutRow go={go} who={name} />
      </div>
    </Page>
  );
}

function NurseryOverview({ go }: { go: (s: Screen) => void }) {
  const [boosted, setBoosted] = useState(false);
  const [bar, setBar] = useState(6);
  const views = [128, 192, 160, 256, 205, 297, 346];
  const trends: [string, number, string][] = [["Lavender", 92, "+38%"], ["Tomato seedlings", 74, "+22%"], ["Rain barrels", 58, "+15%"], ["Native wildflowers", 41, "+9%"]];
  return <Page screen="nursery-overview" role="nursery" go={go}><div className="content"><Header eyebrow="Green Garden Nursery" title="Good morning!" caption="Sales, stock and community interest—all in one place." onBell={() => go("nursery-notifications")}/><ContextStrip icon="chart" title="Store momentum" text="Views are up 12% compared with last week"/><section className="hero store-live"><Topo/><i className="icon-tile green"><Icon name="globe"/></i><div><h2>Your greener storefront is open</h2><p>Twelve products are visible to nearby customers.</p></div></section><div className="business-metrics">{[["1,284","Store views","+12%"],["346","Product taps","+8%"],["7","New orders","3 need you"],["42","Offers used",""]].map(([value,label,note],i)=><section className={i===2?"attention":""} key={label}><span>{label}</span><b>{value}</b>{note&&<small>{note}</small>}</section>)}</div><div className="action-row"><Button onClick={() => go("products")}><Icon name="plus" size={18}/>Add product</Button><Button kind="outline" onClick={() => go("store")}>Edit store</Button></div><section className="card store-chart"><div><h2>Store views this week</h2><Pill>+12%</Pill></div><p className="chart-readout"><b>{views[bar]}</b> views on {["Monday","Tuesday","Wednesday","Thursday","Friday","Saturday","Sunday"][bar]}</p><div>{views.map((v,i)=><button key={i} className={bar===i?"sel":""} onClick={()=>setBar(i)}><i style={{height:v/346*76}}/><b>{["M","T","W","T","F","S","S"][i]}</b></button>)}</div></section><SectionHead title="What neighbours are searching"/><section className="card demand-radar"><p>Searches within 3 km this week. Stock up before the weekend rush.</p>{trends.map(([t,v,c])=><div key={t}><span><b>{t}</b><em>{c}</em></span><i><s style={{width:`${v}%`}}/></i></div>)}</section><section className="plant-week"><img src={PHOTO_PRODUCT} alt="Native herb collection"/><div><span className="eyebrow">Plant of the week</span><h2>Native Herb Collection</h2><p>{boosted ? "Boosted to 1,200 nearby gardeners until Sunday." : "Matches the top 2 searches near you."}</p><Button className="compact" kind={boosted ? "outline" : "primary"} onClick={()=>setBoosted(!boosted)}>{boosted ? "Boost active" : "Boost for 50 pts"}</Button></div></section><SectionHead title="Needs your attention"/><section className="attention order-alert"><div><span className="eyebrow">Order #1048</span><h2>Tomorrow’s pickup needs confirmation</h2><p>Lina M. · 2 items · $42.50</p></div><Button kind="icon" label="View order" onClick={() => go("orders")}><Icon name="chevron"/></Button></section></div></Page>;
}

function PlantArt({ variant = 0 }: { variant?: number }) {
  return <span className={`plant-art v${variant}`}><i className="pot"/><i className="leaf l1"/><i className="leaf l2"/><i className="leaf l3"/></span>;
}

function Products({ go }: { go: (s: Screen) => void }) {
  const [q, setQ] = useState("");
  const [seg, setSeg] = useState("All");
  const products = [["Native Herb Collection","Native herbs · 6 pots","$24.00","LIVE",18],["Water-wise Indoor Plant","Low-water indoor plant","$18.50","LIVE",6],["Peat-free Potting Mix","12 L bag","$12.00","HIDDEN",0],["Wildflower Seed Bomb","Pack of 10 · pollinator mix","$8.00","LIVE",42]] as const;
  const shownP = products.filter(p => p[0].toLowerCase().includes(q.toLowerCase()) && (seg === "All" || (seg === "Live" ? p[3] === "LIVE" : p[4] < 8)));
  return <Page screen="products" role="nursery" go={go}><TopBar title="Products" action={<Icon name="plus"/>} actionLabel="Add product"/><div className="content top-content"><ContextStrip icon="alert" title="One product needs action" text="Restock it to make the listing visible again" tone="peach"/><Stats items={[["12","Live"],["2","Hidden"],["1","Out of stock"]]}/><label className="search"><Icon name="search" size={18}/><input value={q} onChange={e=>setQ(e.target.value)} placeholder="Find a product or collection"/></label><div className="segments">{["All","Live","Needs action"].map(x=><Button key={x} className={seg===x?"active":""} kind="text" onClick={()=>setSeg(x)}>{x}</Button>)}</div><section className="attention stock-alert"><Icon name="alert"/><div><h2>Peat-free Potting Mix is out of stock</h2><p>Add inventory to return this product to the marketplace.</p></div><Button className="compact" onClick={() => go("product-detail")}>Restock</Button></section><div className="list-card product-list">{shownP.length===0&&<p className="channel-empty">No products match.</p>}{shownP.map(([name,desc,price,status,qty],i)=><Button kind="text" key={name} onClick={() => go("product-detail")}><PlantArt variant={i%3}/><span><Pill tone={status==="LIVE"?"sage":"mist"}>{status}</Pill><b>{name}</b><small>{desc}</small><span className="stock-meter"><i><s className={qty===0?"out":qty<8?"low":""} style={{width:`${Math.min(100,qty/40*100)}%`}}/></i><small>{qty===0?"Out of stock":`${qty} in stock`}</small></span><em>{price}</em></span><Icon name="chevron"/></Button>)}</div></div></Page>;
}

function ProductDetail({ go }: { go: (s: Screen) => void }) {
  const [stock, setStock] = useState(0);
  const [live, setLive] = useState(false);
  return <Page screen="product-detail" go={go}><DetailTop title="Edit product" back="products" go={go}/><div className="product-hero"><img src={PHOTO_GREENHOUSE} alt="Peat-free potting mix among nursery plants"/><Button kind="icon" label="Change product image"><Icon name="plus"/></Button></div><div className="content detail-content"><div className="product-title-row"><div><Pill tone={live ? "sage" : "peach"}>{live ? "LIVE" : "OUT OF STOCK"}</Pill><h1>Peat-free Potting Mix</h1><p>12 L bag · Sustainable growing medium</p></div><b>$12.00</b></div><section className="card inventory-control"><div><h2>Inventory</h2><p>Update available units</p></div><div className="stepper"><Button kind="outline" onClick={() => setStock(Math.max(0, stock - 1))}>−</Button><b>{stock}</b><Button kind="outline" onClick={() => setStock(stock + 1)}>+</Button></div><div className="quick-stock">{[10,25,50].map(n=><button key={n} onClick={()=>setStock(stock+n)}>+{n}</button>)}<small>{stock>0?`About ${Math.max(1,Math.round(stock/4))} days of sales`:"Sells about 4 a day"}</small></div></section><section className="card eco-score"><div className="eco-dial"><b>A</b><small>Eco score</small></div><ul><li><Icon name="check" size={14}/>No peat, renewable coir fibres</li><li><Icon name="check" size={14}/>Compost sourced within 40 km</li><li><Icon name="check" size={14}/>Bag returnable for 20 EcoPoints</li></ul></section><section className="card listing-preview"><span className="eyebrow">Marketplace preview</span><p>A peat-free blend for containers, herbs and indoor plants. Made with renewable fibres and local compost.</p><div><Pill><Icon name="leaf" size={14}/>Peat-free</Pill><Pill><Icon name="shield" size={14}/>Eco verified</Pill></div></section><Button className="full" onClick={() => { setLive(stock > 0); }}>{live && stock > 0 ? "Published · update stock" : stock > 0 ? "Save and publish" : "Add stock to publish"}</Button></div></Page>;
}

function Orders({ go }: { go: (s: Screen) => void }) {
  const [steps, setSteps] = useState<Record<string, number>>({ "#1048": 1, "#1047": 2, "#1044": 3 });
  const [tab, setTab] = useState("Active");
  const orders = [["#1048","Lina M.","Tomorrow, 10–12","2 items","$42.50","Herb collection, potting mix"],["#1047","Omar B.","Today, 4–6","1 item","$18.50","Water-wise indoor plant"],["#1044","Nadia R.","May 10","3 items","$54.00","Seed bombs, herbs, mix"]] as const;
  const labels = ["", "NEW", "READY", "COMPLETED"];
  const visible = orders.filter(o => tab === "Active" ? steps[o[0]] < 3 : tab === "Completed" ? steps[o[0]] === 3 : false);
  const revenue = orders.filter(o => steps[o[0]] === 3).reduce((a, o) => a + parseFloat(o[4].slice(1)), 0);
  return <Page screen="orders" role="nursery" go={go}><TopBar title="Orders"/><div className="content top-content"><ContextStrip icon="clock" title="Next pickup · Today, 4–6 PM" text="Order #1047 is packed and ready"/><section className="order-summary"><div><b>${revenue.toFixed(2)}</b><small>Collected this week</small></div><div><b>{orders.filter(o=>steps[o[0]]<3).length}</b><small>Still open</small></div><div><b>14</b><small>Pots returned</small></div></section><div className="segments order-segments">{["Active","Completed","Cancelled"].map(x=><Button key={x} className={tab===x?"active":""} kind="text" onClick={()=>setTab(x)}>{x}{x==="Active"&&<i>{orders.filter(o=>steps[o[0]]<3).length}</i>}</Button>)}</div><div className="orders">{visible.length===0&&<p className="channel-empty">{tab==="Cancelled"?"No cancelled orders. Nice work.":"Nothing here yet."}</p>}{visible.map(([number,name,pickup,items,price,what])=>{const step=steps[number];const status=labels[step];return <section className="order-card" key={number}><div className="order-top"><b>Order {number}</b><Pill tone={status==="NEW"?"peach":status==="READY"?"sage":"mist"}>{status}</Pill></div><h2>{name}</h2><p>{what}</p><p className="pickup"><Icon name="clock" size={14}/>Pickup · {pickup}</p><div className="steps">{[1,2,3].map(x=><i className={x<=step?"done":""} key={x}/>)}</div><div className="step-labels"><small className={step>=1?"on":""}>Received</small><small className={step>=2?"on":""}>Packed</small><small className={step>=3?"on":""}>Picked up</small></div><footer><span>{items} · <b>{price}</b></span>{step<3?<Button className="compact" onClick={()=>setSteps({...steps,[number]:step+1})}>{step===1?"Mark packed":"Confirm pickup"}</Button>:<Button kind="text" onClick={()=>setSteps({...steps,[number]:2})}>Undo</Button>}</footer></section>;})}</div></div></Page>;
}

function Store({ go }: { go: (s: Screen) => void }) {
  const [open, setOpen] = useState(true);
  const [rev, setRev] = useState(0);
  const reviews = [["Lina M.", "Staff helped me choose plants for a shady balcony. Everything is thriving!"], ["Omar B.", "Love the pot return scheme. Got EcoPoints back on my next visit."], ["Nadia R.", "Best native wildflower selection in town."]];
  return <Page screen="store" role="nursery" go={go}><TopBar title="Public store"/><div className="store-cover"><img src={PHOTO_STORE} alt="Green Garden Nursery filled with plants"/><i className="photo-overlay"/><Topo/></div><div className="content store-content"><span className="business-logo">GG</span><ContextStrip icon="globe" title="Public preview" text="This page is live in the Eco Marketplace"/><Pill>Verified Eco Partner</Pill><h1>Green Garden Nursery</h1><p className="store-address"><Icon name="pin" size={16}/>14 Grove Lane · {open ? "Open until 6:00 PM" : "Closed for today"}</p><button className={`open-toggle ${open?"on":""}`} onClick={()=>setOpen(!open)}><i/><span>{open ? "Accepting pickups" : "Paused · customers see Closed"}</span></button><p>Native plants, thoughtful supplies and practical help for greener homes.</p><div className="store-values"><Pill>70% native stock</Pill><Pill>Peat-free soil</Pill><Pill>Pot returns</Pill></div><section className="attention customer-note"><Icon name="eye"/><b>This is what customers see</b></section><section className="card rating"><b>4.8</b><div><h2>Neighbours love this shop</h2><p>Based on 86 community ratings</p></div></section><section className="store-impact"><div><b>1,240</b><small>pots returned</small></div><div><b>3.2t</b><small>peat avoided</small></div><div><b>580</b><small>native plants sold</small></div></section><SectionHead title="What customers say"/><section className="review-card"><p>“{reviews[rev][1]}”</p><footer><b>{reviews[rev][0]} · ★★★★★</b><span>{reviews.map((_,i)=><button key={i} aria-label={`Review ${i+1}`} className={rev===i?"on":""} onClick={()=>setRev(i)}/>)}</span></footer></section><SectionHead title="Featured products" action="Manage"/><div className="featured-products"><section><img src={PHOTO_PRODUCT} alt="Native herb collection"/><b>Native Herb Collection</b><span>$24.00</span></section><section><img src={PHOTO_GREENHOUSE} alt="Water-wise indoor plants"/><b>Water-wise Indoor Plant</b><span>$18.50</span></section></div></div></Page>;
}

function HoursCard() {
  const [open, setOpen] = useState([true, true, true, true, true, true, false]);
  return <section className="hours-card">{["Mon","Tue","Wed","Thu","Fri","Sat","Sun"].map((d, i) => <button key={d} className={open[i] ? "on" : ""} onClick={() => setOpen(open.map((v, j) => j === i ? !v : v))}><b>{d}</b><small>{open[i] ? (i === 5 ? "9–4" : "8–6") : "Closed"}</small></button>)}</section>;
}

function BusinessProfile({
  go,
  user,
  onUpdateAvatar,
}: {
  go: (s: Screen) => void;
  user?: UserProfile;
  onUpdateAvatar?: (url: string) => void;
}) {
  const name = user?.name || "Green Garden Nursery";
  const initials = name
    .split(" ")
    .map((w) => w[0])
    .slice(0, 2)
    .join("")
    .toUpperCase() || "GG";

  return (
    <Page screen="business-profile" role="nursery" go={go}>
      <TopBar title="Business profile" action={<Icon name="gear" />} user={user} />
      <div className="content top-content">
        <ContextStrip icon="shield" title="Eco Partner status active" text="Next verification review in 84 days" />
        <ProfileCover
          photo={PHOTO_STORE}
          initials={initials}
          name={user?.farmName || name}
          sub="14 Grove Lane · Since 2016"
          tag="Eco Partner"
          square
          avatarPhoto={user?.avatar}
          onUploadAvatar={onUpdateAvatar}
        />
        <Stats
          items={[
            ["12", "Products"],
            ["4.8", "Rating"],
            ["42", "Offers used"],
          ]}
        />
        <section className="tier-card">
          <Topo />
          <div className="tier-head">
            <div>
              <span className="eyebrow">Eco Partner tier</span>
              <h2>Silver Grower</h2>
            </div>
            <span className="tier-badge">
              <Icon name="leaf" size={20} />
            </span>
          </div>
          <div className="tier-path">
            {["Seed", "Silver", "Gold", "Forest"].map((t, i) => (
              <span key={t} className={i <= 1 ? "on" : ""}>
                <i />
                {t}
              </span>
            ))}
          </div>
          <Progress value={68} />
          <p>320 points to <b>Gold</b>. Add 2 more native products or reach 50 pot returns.</p>
        </section>
        <section className="payout-card">
          <div>
            <span className="eyebrow">Next payout · Friday</span>
            <b>$1,284.50</b>
            <small>From 31 orders this week</small>
          </div>
          <Button className="compact" kind="outline">Details</Button>
        </section>
        <SectionHead title="Opening hours" />
        <HoursCard />
        <SectionHead title="Team" />
        <section className="team-row">
          {[
            ["LA", name.split(" ")[0] || "Layla", "Owner"],
            ["KM", "Karim", "Plant care"],
            ["RN", "Rana", "Orders"],
          ].map(([i, n, r]) => (
            <div key={n}>
              <span className="rank-av">{i}</span>
              <b>{n}</b>
              <small>{r}</small>
            </div>
          ))}
          <button>
            <Icon name="plus" size={18} />
            <small>Invite</small>
          </button>
        </section>
        <SectionHead title="Notifications" />
        <PrefToggles
          items={[
            ["New orders", "Instant alert for every order", true],
            ["Low stock", "When a product drops under 8 units", true],
            ["Customer reviews", "When someone rates your shop", false],
          ]}
        />
        <MenuList
          items={[
            ["globe", "Store information"],
            ["gift", "Offers and EcoPoints"],
            ["shield", "Verification and policies"],
            ["chat", "Business support"],
          ]}
        />
        <LogoutRow go={go} who={user?.farmName || name} />
      </div>
    </Page>
  );
}

function AppContent() {
  const farm = useFarmStore();
  const [currentUser, setCurrentUser] = useState<UserProfile>(() => getInitialUser());
  const citizen = useCitizenStore(currentUser.name);

  const [screen, setScreen] = useState<Screen>(() => {
    const isAuth = typeof window !== "undefined" && window.sessionStorage.getItem("eco-authenticated") === "true";
    if (!isAuth) return "signup";
    const user = getInitialUser();
    return user.role === "citizen" ? "home" : user.role === "nursery" ? "nursery-overview" : "farmer-overview";
  });

  const go = (next: Screen) => {
    const isPublic = next === "signup" || next === "welcome";
    const isAuth = typeof window !== "undefined" && window.sessionStorage.getItem("eco-authenticated") === "true";
    const destination = isPublic || isAuth ? next : "signup";
    setScreen(destination);
    window.scrollTo({ top: 0, left: 0, behavior: "auto" });
  };

  const handleUserChange = (u: UserProfile) => {
    setCurrentUser(u);
    if (typeof window !== "undefined") {
      window.sessionStorage.setItem("eco-user", JSON.stringify(u));
      window.sessionStorage.setItem("eco-authenticated", "true");
      window.localStorage.setItem("eco-user-profile", JSON.stringify(u));
    }
  };

  const handleUpdateAvatar = (url: string) => {
    const updated: UserProfile = { ...currentUser, avatar: url };
    handleUserChange(updated);
  };

  const screens: Record<Screen, React.ReactNode> = {
    welcome: <Welcome go={go} />,
    signup: <Signup go={go} onUserChange={handleUserChange} currentUser={currentUser} />,
    home: <Home go={go} user={currentUser} citizen={citizen} />,
    explore: <Explore go={go} citizen={citizen} />,
    learn: <Learn go={go} />,
    community: <Community go={go} />,
    profile: <Profile go={go} user={currentUser} citizen={citizen} onUpdateAvatar={handleUpdateAvatar} />,
    mission: <MissionDetail go={go} citizen={citizen} />,
    rewards: <Rewards go={go} user={currentUser} citizen={citizen} />,
    notifications: <Notifications go={go} />,
    "farmer-notifications": <Notifications go={go} screen="farmer-notifications" back="farmer-overview" role="farmer" />,
    "nursery-notifications": <Notifications go={go} screen="nursery-notifications" back="nursery-overview" role="nursery" />,
    "farmer-overview": <FarmerOverview go={go} farm={farm} user={currentUser} />,
    fields: <Fields go={go} farm={farm} user={currentUser} />,
    risks: <Risks go={go} farm={farm} />,
    advice: <Advice go={go} farm={farm} />,
    assistant: <Assistant go={go} farm={farm} user={currentUser} />,
    "farm-profile": <FarmProfile go={go} farm={farm} user={currentUser} onUpdateAvatar={handleUpdateAvatar} />,
    "nursery-overview": <NurseryOverview go={go} />,
    products: <Products go={go} />,
    "product-detail": <ProductDetail go={go} />,
    orders: <Orders go={go} />,
    store: <Store go={go} />,
    marketplace: <Marketplace go={go} />,
    "business-profile": <BusinessProfile go={go} user={currentUser} onUpdateAvatar={handleUpdateAvatar} />,
  };

  return (
    <div className="app-shell">
      {citizen.toastMessage && (
        <div className="eco-toast">
          <span>{citizen.toastMessage}</span>
        </div>
      )}
      {screens[screen]}
    </div>
  );
}

export default function App() {
  if (CLERK_PUBLISHABLE_KEY) {
    return (
      <ClerkProvider publishableKey={CLERK_PUBLISHABLE_KEY}>
        <AuthTokenSync />
        <AppContent />
      </ClerkProvider>
    );
  }
  return <AppContent />;
}
